import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as echarts from 'echarts'; // ECharts library is assumed to be available
import * as api from '../api';
import VirtualAssistant from './VirtualAssistant';
import tutorialLogo from '../assets/Group11887.png';
import { logger } from '../utils/logger';
import * as dashboardCache from '../utils/dashboardCache';

const mixedHeaderSrc = '/headers-footers/mixedheader.svg';
const mixedFooterSrc = '/images/logos/logo.svg';
const DASHBOARD_VARIANT = 'hybrid';

// --- Market Status Checker (now fetched from API) ---
// Removed local implementation - now using API call


// --- 1. CONSTANTS AND CONFIGURATION (Outside Component) ---
// Note: Most data is now fetched from API, keeping only UI-related constants

// --- NEW: SUMMARY STATUS COLORS ---
const PORTFOLIO_SUMMARY_STATUS_COLORS = {
    defensive: {
        color: 'text-green-400',
        bgColor: 'bg-green-900/50',
        borderColor: 'border-green-500',
        icon: (
            <svg className="flex-shrink-0 w-6 h-6 mr-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
        )
    },
    balanced: {
        color: 'text-blue-400',
        bgColor: 'bg-blue-900/50',
        borderColor: 'border-blue-500',
        icon: (
            <svg className="flex-shrink-0 w-6 h-6 mr-3 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0c-1.11 0-2.08-.402-2.599-1M12 16v1m0-1c-1.657 0-3-.895-3-2s1.343-2 3-2 3-.895 3-2 1.343-2 3-2m0 8c1.11 0 2.08.402 2.599 1M12 16v-1m0 1v-8" />
            </svg>
        )
    },
    aggressive: {
        color: 'text-red-400',
        bgColor: 'bg-red-900/50',
        borderColor: 'border-red-500',
        icon: (
            <svg className="flex-shrink-0 w-6 h-6 mr-3 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        )
    }
};


// --- CORRELATION SCATTER DATA (now fetched from API) ---
// Removed local generation - now using API call

// --- ASSET ALLOCATION DATA (now fetched from API) ---
// Removed local generation - now using API call

// Helper function to map API allocation to frontend format
const mapAllocationFromAPI = (apiAllocation) => {
    return apiAllocation.map(item => {
        let colorVar = '--asset-spx';
        let ticker = item.ticker;
        
        if (item.name.includes('Leveraged')) {
            colorVar = '--asset-tqqq';
            ticker = 'TQQQ';
        } else if (item.name.includes('Risk Free') || item.name.includes('Risk free')) {
            colorVar = '--asset-cash';
            ticker = 'T-bill or cash';
        } else {
            ticker = 'SPY or VOO';
        }
        
        return {
            name: item.name,
            ticker: ticker,
            percent: item.percent,
            colorVar: colorVar
        };
    });
};

// --- OPPORTUNITY CHART DATA (now fetched from API) ---
// Removed local generation - now using API call


// --- STRATEGY INSIGHTS TEXT (REMOVED, Replaced by Module 4) ---
// const getStrategyInsight = (risk, opportunityPercent) => { ... };


// Risk profiles will be loaded from API, but we keep the static UI configuration
const getRiskProfileConfig = (apiProfile, performanceData, descriptions = {}) => {
    const configs = {
        'Averse': {
            title: descriptions?.Averse?.title || 'Averse',
            cssClass: 'handle-color-averse',
            scatterColorVar: '--handle-averse-color',
            position: '0%',
            description: (
                <div className="text-gray-400 space-y-4">
                    {(() => {
                        const label = descriptions?.Averse?.label || 'Risk Averse';
                        const paragraphs = descriptions?.Averse?.paragraphs || [
                            'will set our model to focus on capital preservation, taking only the most conservative positions. Risk-reward will be minimized, prioritizing stability over growth.',
                            'You can expect low volatility and minimal losses, but returns will be modest, typically tracking slower market growth. This profile is ideal for investors prioritizing security.'
                        ];
                        const remainingParagraphs = paragraphs.slice(1);

                        return (
                            <>
                                <p>
                                    <span className="profile-title" style={{ color: 'var(--averse-title-color)' }}>{label}</span> {paragraphs[0]}
                                </p>
                                {remainingParagraphs.map((text, index) => (
                                    <p key={index}>{text}</p>
                                ))}
                            </>
                        );
                    })()}
                </div>
            ),
        },
        'Neutral': {
            title: descriptions?.Neutral?.title || 'Neutral',
            cssClass: 'handle-color-neutral',
            scatterColorVar: '--handle-neutral-color',
            position: '33.33%',
            description: (
                <div className="text-gray-400 space-y-4">
                    {(() => {
                        const label = descriptions?.Neutral?.label || 'Risk Neutral';
                        const paragraphs = descriptions?.Neutral?.paragraphs || [
                            'will balance capital preservation and growth, taking measured positions in established markets. Risk-reward will be balanced, aiming for consistent, moderate returns.',
                            'You can expect moderate volatility and acceptable drawdowns, with returns aiming to outperform standard market benchmarks over the long term.'
                        ];
                        const remainingParagraphs = paragraphs.slice(1);

                        return (
                            <>
                                <p>
                                    <span className="profile-title" style={{ color: 'var(--neutral-title-color)' }}>{label}</span> {paragraphs[0]}
                                </p>
                                {remainingParagraphs.map((text, index) => (
                                    <p key={index}>{text}</p>
                                ))}
                            </>
                        );
                    })()}
                </div>
            ),
        },
        'Seeker': {
            title: descriptions?.Seeker?.title || 'Seeker',
            cssClass: 'handle-color-seeker',
            scatterColorVar: '--handle-seeker-color',
            position: '66.66%',
            description: (
                <div className="text-gray-400 space-y-4">
                    {(() => {
                        const label = descriptions?.Seeker?.label || 'Risk Seeker';
                        const paragraphs = descriptions?.Seeker?.paragraphs || [
                            'will set our model to focus on maximizing gains, by taking more aggressive positions when it identifies opportunities. Risk-reward will be optimized, but at a higher risk-taking threshold.',
                            'You can expect higher returns when opportunities arise, but also higher volatility and deeper losses, if the market falls.'
                        ];
                        const remainingParagraphs = paragraphs.slice(1);

                        return (
                            <>
                                <p>
                                    <span className="profile-title" style={{ color: 'var(--seeker-label-color)' }}>{label}</span> {paragraphs[0]}
                                </p>
                                {remainingParagraphs.map((text, index) => (
                                    <p key={index}>{text}</p>
                                ))}
                            </>
                        );
                    })()}
                </div>
            ),
        }
    };
    
    const config = configs[apiProfile] || configs['Averse'];
    return {
        ...config,
        performanceData: performanceData || [],
        baseAllocation: apiProfile ? { spy: 80, tqqq: 5, cash: 15 } : { spy: 80, tqqq: 5, cash: 15 }
    };
};


// --- OPPORTUNITY CHART DATA & LOGIC ---
const getOpportunityDegree = (percent, labels = {}) => {
    if (percent > 80) return labels.veryStrong || "VERY STRONG";
    if (percent > 60) return labels.strong || "STRONG";
    if (percent >= 40) return labels.reasonable || "REASONABLE";
    if (percent >= 30) return labels.weak || "WEAK";
    return labels.veryWeak || "VERY WEAK";
};

// --- ALERTS COMPONENT LOGIC (REMOVED) ---
/*
const getAlertStatus = (risk, opportunityPercent) => {
    // ... (Content removed as it's no longer used)
};
*/

// --- ECharts Logic ---

// --- 2. DYNAMIC CSS INJECTION FUNCTION ---
const injectStyles = () => {
    // Remove any existing dashboard styles first
    const existingStyle = document.getElementById('dashboard-hybrid-styles');
    if (existingStyle) {
        existingStyle.remove();
    }
    
    const style = document.createElement('style');
    style.id = 'dashboard-hybrid-styles'; // Add ID for easy removal
    style.innerHTML = `
        /* Definición de la fuente y colores */
        :root {
            --dark-bg: #1e1e1e;
            --main-bg: #212529; /* Fondo principal del Dashboard */
            --text-light: #f0f0f0;
            --text-primary: #85c8ff; /* Azul claro (General UI) */
            --seeker-label-color: #f7a68e; /* Rosa/Salmón para el texto Seeker */
            --neutral-title-color: #a4c4f0; /* Azul neutro (usado solo en el texto de descripción) */
            
            /* COLORES PARA EL HANDLE */
            --handle-averse-color: #5cb85c; /* Verde para Averse */
            --handle-neutral-color: #85c8ff; /* Azul claro para Neutral (Handle) */
            --handle-seeker-color: #f7a68e; /* Rojo pálido/Salmón para Seeker (Handle) */

            /* NEW: Faded colors for badges */
            --handle-averse-color-faded: rgba(92, 184, 92, 0.2);
            --handle-neutral-color-faded: rgba(133, 200, 255, 0.2);
            --handle-seeker-color-faded: rgba(247, 163, 142, 0.2);

            /* COLOR DE MARCA Y FOOTER (AZUL #3197C8) */
            --footer-color: #3197C8; 

            /* COLORES ESPECÍFICOS DEL MÓDULO 1 */
            --module-bg: var(--main-bg); 
            --chart-panel-bg: #2a3138; 
            --empty-donut-color: rgba(60, 60, 60, 0.7); /* Gris oscuro con 70% de transparencia */
            
            /* NUEVOS COLORES DE EXPOSICIÓN */
            --asset-spx: var(--footer-color); /* #3197C8 - Blue */
            --asset-tqqq: #F0A31D; 
            --asset-cash: #4CAF50; 
            --asset-text: #cccccc;

            /* GAUGE COLORS - CORRECTED */
            --gauge-fill-color: #343a40; /* Dark Grey (Empty portion) */
            --gauge-empty-color: #ffffff; /* White (Filled portion) */
            --gauge-center-text: #f0f0f0; /* White text */

            /* Color del título AVERSE ajustado para que sea verde */
            --averse-title-color: var(--handle-averse-color); 
        }
        
        /* Estilos base del Layout */
        body {
            font-family: 'Inter', sans-serif;
            background-color: var(--main-bg); 
            min-height: 10vh;
            display: flex;
            flex-direction: column;
        }

        /* --- MÓDULO SUPERIOR FIJO (HEADER Y RISK PROFILER) --- */
        .fixed-top-module {
            width: 100%;
            padding: 20px 0 10px 0; 
            position: fixed;
            top: 0;
            z-index: 50; 
            /* Glassmorphism Effect */
            background-color: rgba(33, 37, 41, 0.9); /* main-bg con opacidad */
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.6); /* Sombra más profunda */
        }

        .dashboard-content-wrapper {
             max-width: 900px; /* Mantener un ancho razonable para el módulo superior fijo */
             margin: 0 auto;
             padding: 0 1rem;
        }
        
        /* Estilo del logo AN */
        .logo-an-bg {
            background-color: var(--footer-color); 
            color: var(--text-light); 
            padding: 2px 5px;
            border-radius: 4px;
            font-size: 1.25rem;
            font-weight: 700;
            line-height: 1;
        }

        /* Estilo para el texto NATIVE */
        .logo-native-color {
            color: var(--footer-color); 
        }

        /* Estilo del separador */
        .custom-hr {
            border: none;
            height: 1px;
            background-color: #3b3b3b;
            margin-top: 1.5rem;
            margin-bottom: 2.5rem;
        }

        /* --- CONTENIDO SCROLLEABLE --- */
        .scrollable-dashboard-body {
            /* Initial padding matches the expanded header height (approx 260px + 30px safety) */
            padding-top: 290px; 
            padding-bottom: 50px; 
            overflow-y: auto; 
            flex-grow: 1; 
            width: 100%; 
            transition: padding-top 0.3s ease;
        }
        
        /* ADJUST PADDING FOR MAX COLLAPSED STATE (approximate reduction to 100px) */
        .scrollable-dashboard-body.collapsed {
            /* 100px is safe for the minimized header (~80px tall) */
            padding-top: 100px; 
        }

        /* Contenedor interno del body para centrar y aplicar max-width */
        .scrollable-inner-container {
             max-width: 900px; /* Ancho para el contenido principal */
             margin: 0 auto;
             padding: 0 1rem;
        }


        /* --- MÓDULO INFERIOR FIJO (FOOTER) --- */
        .footer-bar {
            background-color: #1f1f1f; 
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            height: 40px;
            z-index: 50;
            display: flex;
            justify-content: center;
            align-items: center;
        }
        
        /* SLIDER SIMULADO (Risk Profiler) */
        .risk-slider-container {
            position: relative;
            height: 28px;
            width: 100%;
            border-radius: 14px;
            background-color: rgba(30, 30, 30, 0.6); 
            overflow: hidden;
            cursor: pointer; 
            border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .slider-handle-active {
            position: absolute;
            height: 100%;
            width: 33.34%;
            border-radius: 14px;
            box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.3);
            transition: left 0.3s cubic-bezier(0.68, -0.55, 0.27, 1.55), background-color 0.3s ease; 
        }
        
        .handle-color-averse { background-color: var(--handle-averse-color); }
        .handle-color-neutral { background-color: var(--handle-neutral-color); }
        .handle-color-seeker { background-color: var(--handle-seeker-color); }
        
        .risk-label {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            font-size: 0.875rem;
            font-weight: 500;
            color: #b0b0b0; 
            padding: 0 10px;
            transition: all 0.3s ease;
            line-height: 1.4;
            width: 33.33%;
            display: flex;
            justify-content: center;
        }

        .label-averse { left: 0; margin-left: 0; }
        .label-neutral { left: 33.33%; }
        .label-seeker { left: 66.66%; margin-right: 0; }

        .risk-label.active {
            font-weight: 700;
            color: var(--text-light); 
            background-color: transparent !important; 
            padding: 0 10px; 
            border-radius: 0;
        }
        
        .label-seeker.active { 
            color: #222; /* Darker text for contrast on bright handle */
            background-color: transparent !important;
        }


        .risk-description .profile-title {
            font-weight: bold;
            display: inline;
            margin-right: 5px;
        }
        .risk-description p {
             font-size: 0.9em;
             color: #b0b0b0;
             margin-top: 0;
        }
        
        /* Estilo General para Módulos */
        .dashboard-module {
            background-color: var(--module-bg); 
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
            color: #f0f0f0;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2); 
            min-height: 10rem; 
            /* Micro-Animation: Transition for lift/shadow */
            transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        
        .dashboard-module:hover {
            transform: translateY(-3px);
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
        }

        /* --- MÓDULO 1: PORTFOLIO & PERFORMANCE --- */
        .portfolio-module {
            background-color: #2a3138; /* Slightly darker background for this section */
            display: flex;
            flex-direction: column; /* Start as column for mobile */
            /* gap: 20px; */ /* Gap is now handled by the wrapper */
            padding: 24px;
        }
        
        /* Item de Asignación */
        .asset-item {
            display: flex;
            align-items: center;
            margin-bottom: 10px;
            background-color: #212529; /* Main background for contrast */
            padding: 15px;
            border-radius: 8px;
            transition: transform 0.2s ease, box-shadow 0.2s ease;
            cursor: pointer;
        }

        .asset-item:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
        }
        
        /* Estilos de las Donas (Gráficos Circulares) - Simulated */
        .donut-chart {
            width: 70px; 
            height: 70px;
            border-radius: 50%;
            position: relative;
            margin-right: 20px;
            box-sizing: border-box;
            flex-shrink: 0;
            /* Container for rotation - separate transform context */
        }

        .donut-chart::before {
            content: '';
            position: absolute;
            width: 50px; 
            height: 50px;
            top: 10px;
            left: 10px;
            background-color: var(--module-bg); 
            border-radius: 50%;
            z-index: 1;
        }
        
        .donut-percent {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(90deg);
            z-index: 10;
            font-size: 1.125rem;
            font-weight: bold;
            color: var(--text-light); /* Color BLANCO */
            white-space: nowrap;
            pointer-events: none;
        }

        /* Performance Chart Column */
        .performance-column {
            flex: 2;
            background-color: var(--chart-panel-bg); 
            border-radius: 8px;
            padding: 20px;
            position: relative;
            height: 350px; 
        }
        
        /* Scatter Chart Specific Styles */
        .scatter-chart-container {
            width: 100%;
            height: 350px; /* Consistent height for charts */
            min-height: 300px; /* FALLBACK HEIGHT ADDED HERE */
            background-color: var(--chart-panel-bg); 
            border-radius: 8px;
            padding: 20px;
        }


        /* Exposure Chart Container Specifics */
        .exposure-chart-wrapper {
            width: 100%;
            height: 325px; 
            max-width: 400px; /* Limit chart size */
            margin: 0 auto;
        }
        
        /* Contenedor del Chart de ECharts (Performance) */
        #echartContainer {
            position: absolute;
            width: calc(100% - 40px); 
            height: calc(100% - 70px); 
            top: 40px; 
            left: 20px; 
            z-index: 5;
            box-sizing: border-box; 
        }

        /* Leyendas superiores */
        .chart-legends-top {
            position: absolute;
            top: 20px;
            left: 20px; 
            display: flex;
            gap: 10px;
            font-size: 0.75rem;
            font-weight: 500;
            z-index: 10; 
        }

        .chart-legend-item {
            display: flex;
            align-items: center;
            padding: 4px 8px;
            border-radius: 4px;
            color: #f0f0f0;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4); 
        }

        .legend-spy {
            background-color: rgba(255, 255, 255, 0.2);
            color: #f0f0f0;
        }
        .legend-native {
            background-color: var(--footer-color); 
            color: #f0f0f0;
        }

        /* Eje X (Título) */
        .chart-x-axis {
            position: absolute; 
            bottom: 10px; 
            height: 20px; 
            width: 100%; 
            display: flex;
            justify-content: center; 
            align-items: center;
            padding-top: 5px; 
            z-index: 20;
        }
        
        /* Descripción del gráfico */
        .chart-description {
            font-size: 0.75rem;
            color: #999;
            text-align: center;
            line-height: 1.3;
            padding: 15px 10px 0 10px; 
        }

        /* --- OPPORTUNITY CHART (GAUGE) STYLES --- */
        .new-content-area {
            background-color: transparent;
            height: 100%; 
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
        }
        
        .opportunity-gauge-container {
            width: 100%;
            height: 250px;
            margin-top: 20px;
        }

        /* Slider track is the parent of the input range */
        .opportunity-slider-track {
            height: 8px;
            width: 100%; 
            background-color: #555;
            border-radius: 4px;
            margin-top: 10px; /* Adjusted to 10px */
            position: relative;
            box-shadow: inset 0 1px 3px rgba(0,0,0,0.5);
        }
        
        /* Hide native input range styling */
        .opportunity-slider-input {
            width: 100%;
            height: 100%;
            -webkit-appearance: none;
            appearance: none;
            background: transparent;
            cursor: pointer;
            position: absolute;
            top: 0;
            left: 0;
            opacity: 0; /* Hide the native input */
            z-index: 10;
        }
        
        /* Custom Handle */
        .opportunity-handle {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background-color: #fff; /* White fill */
            border: 2px solid #333; /* Dark Grey rim */
            position: absolute;
            top: -6px; /* Center handle on track */
            z-index: 15;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
            transition: left 0.15s ease-out; /* Smooth movement */
            pointer-events: none; /* Let clicks pass to the invisible input range */
        }
        
        /* Handle Labels */
        .opportunity-label, .opportunity-tooltip {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            white-space: nowrap;
            font-size: 0.8rem;
            padding: 4px 8px;
            border-radius: 4px;
            transition: opacity 0.2s;
            pointer-events: none;
        }
        .opportunity-label {
            top: -28px; /* Above the handle */
            font-weight: bold;
            color: #212529; /* Dark text for contrast */
            background-color: #ffffff; /* White background */
            transform: translate(-50%, -100%); /* Adjust to sit right above the handle */
            z-index: 20;
        }
        .opportunity-tooltip {
            bottom: -30px; /* Below the handle */
            background-color: #333; /* Dark Grey tooltip container */
            color: #fff;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
            transform: translate(-50%, 100%); /* Adjust to sit right below the handle */
            z-index: 20;
        }
        .opportunity-tooltip::after {
            content: "";
            position: absolute;
            top: 100%;
            left: 50%;
            margin-left: -5px;
            border-width: 5px;
            border-style: solid;
            /* Arrow matches container background for seamless look */
            border-color: rgba(33, 37, 41, 0.5) transparent transparent transparent; 
        }

        /* --- NEW INFO TOOLTIP STYLE --- */
        .info-tooltip-container {
            position: relative;
            display: inline-flex; /* Use inline-flex to align icon with text */
            align-items: center;
            cursor: help;
            margin-left: 8px; /* Space from title */
        }

        .info-tooltip-content {
            position: absolute;
            width: 280px; /* Slightly wider for message */
            background-color: rgba(33, 37, 41, 0.5); /* Dark Gray (main-bg) 50% transparent */
            color: var(--text-light);
            text-align: left;
            border-radius: 6px;
            padding: 12px;
            z-index: 100;
            bottom: calc(100% + 15px); /* Position above the icon + space */
            left: 50%;
            transform: translateX(-50%);
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.3s, visibility 0.3s;
            font-size: 0.85rem;
            line-height: 1.4;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.6);
            border: 1px solid rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(4px);
        }

        .info-tooltip-container:hover .info-tooltip-content {
            visibility: visible;
            opacity: 1;
        }

        .info-tooltip-content::after {
            content: "";
            position: absolute;
            top: 100%;
            left: 50%;
            margin-left: -5px;
            border-width: 5px;
            border-style: solid;
            /* Arrow matches container background for seamless look */
            border-color: rgba(33, 37, 41, 0.5) transparent transparent transparent; 
        }
        
        /* UPDATED: Collapsed Bar Style (Alternative 2) */
        .collapsed-bar {
            /* This is the new style */
            display: flex;
            justify-content: space-between;
            align-items: center;
            height: 56px; /* Give it a fixed height */
            background-color: var(--chart-panel-bg); /* Use a solid color */
            border: 1px solid #3b3b3b;
            border-radius: 8px;
            padding: 0; /* Remove padding */
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
            transition: background-color 0.2s;
            cursor: default;
        }
        .collapsed-bar:hover {
            opacity: 1; /* Remove hover opacity */
        }


        /* --- NEW: Tutorial Animations --- */
        @keyframes modalPopIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
        }
        .animate-modal-pop-in {
            animation: modalPopIn 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
        }

        @keyframes modalPopOut {
            from { opacity: 1; transform: scale(1); }
            to { opacity: 0; transform: scale(0.95); }
        }
        .animate-modal-pop-out {
            animation: modalPopOut 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
        }

        @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes iconPopIn {
            0% { opacity: 0; transform: scale(0.5); }
            60% { opacity: 1; transform: scale(1.1); }
            100% { opacity: 1; transform: scale(1); }
        }
        .animate-icon-pop-in {
            animation: iconPopIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
            animation-delay: 100ms; /* Delay icon pop */
            opacity: 0; /* Start hidden */
        }
        .animate-title-fade-in {
            animation: fadeInUp 0.4s ease-out forwards;
            animation-delay: 200ms;
            opacity: 0; /* Start hidden */
        }
        .animate-text-fade-in {
            animation: fadeInUp 0.4s ease-out forwards;
            animation-delay: 250ms;
            opacity: 0; /* Start hidden */
        }

        /* --- NEW: Live Quote Widget Styles --- */
        .quote-item {
            background-color: var(--chart-panel-bg);
            border: 1px solid #3b3b3b;
            border-radius: 8px;
            padding: 12px 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: all 0.2s ease;
        }
        .quote-item:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
            border-color: #555;
        }
        .quote-ticker {
            font-size: 1.125rem; /* 18px */
            font-weight: 600;
            color: var(--text-light);
            display: block;
        }
        .quote-price {
            font-size: 0.875rem; /* 14px */
            color: #aaa;
        }
        .quote-change {
            font-size: 0.875rem; /* 14px */
            font-weight: 600;
            padding: 4px 8px;
            border-radius: 6px;
            min-width: 60px;
            text-align: center;
        }
        .quote-change.positive {
            color: #4ade80; /* Tailwind Green 400 */
            background-color: rgba(74, 222, 128, 0.1);
        }
        .quote-change.negative {
            color: #f87171; /* Tailwind Red 400 */
            background-color: rgba(248, 113, 113, 0.1);
        }

        /* --- NEW: Market Status Indicator Styles --- */
        @keyframes pulse-green {
            0% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7); }
            70% { box-shadow: 0 0 0 10px rgba(74, 222, 128, 0); }
            100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
        }
        @keyframes pulse-red {
            0% { box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.7); }
            70% { box-shadow: 0 0 0 10px rgba(248, 113, 113, 0); }
            100% { box-shadow: 0 0 0 0 rgba(248, 113, 113, 0); }
        }
        .market-status-indicator {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            transition: background-color 0.3s ease;
        }
        .market-status-indicator.open {
            background-color: #4ade80; /* green-400 */
            box-shadow: 0 0 0 0 rgba(74, 222, 128, 1);
            animation: pulse-green 2s infinite;
        }
        .market-status-indicator.closed {
            background-color: #f87171; /* red-400 */
            /* No pulse for closed */
        }

        /* --- NEW: Loading Spinner Styles --- */
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        .dashboard-loading-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(33, 37, 41, 0.85);
            backdrop-filter: blur(4px);
            -webkit-backdrop-filter: blur(4px);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            transition: opacity 0.3s ease, visibility 0.3s ease;
        }
        .dashboard-loading-overlay.hidden {
            opacity: 0;
            visibility: hidden;
        }
        .loading-spinner {
            width: 48px;
            height: 48px;
            border: 3px solid rgba(133, 200, 255, 0.2);
            border-top-color: var(--footer-color);
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
        }
        .loading-text {
            margin-top: 16px;
            color: var(--text-light);
            font-size: 0.875rem;
            font-weight: 500;
            letter-spacing: 0.5px;
        }
        .loading-note {
            margin-top: 8px;
            color: var(--text-light);
            font-size: 0.75rem;
            font-weight: 400;
            opacity: 0.8;
            text-align: center;
        }


        @media (min-width: 768px) {
            /* FIX: Removed flex-wrap from portfolio-module.
              The wrapper div inside handles the row layout.
            */
            .portfolio-module {
                flex-direction: column; /* Revert to column for the outer container */
                gap: 20px; /* Restore gap for mobile and between rows */
            }
            .exposure-chart-wrapper {
                margin: 0 auto; 
            }
        }
    `;
    document.head.appendChild(style);
    return style; // Return style element for cleanup
};

// --- ECharts Opportunity Gauge Component ---
// --- Exposure Timeline Chart Component ---
const ExposureTimelineChart = ({ chartRef, timelineData, currentData, risk, getCssVariable, zoomControlRef }) => {
    const [zoomRange, setZoomRange] = useState({ start: 0, end: 100 });
    const chartInstanceRef = useRef(null);
    const zoomRangeRef = useRef({ start: 0, end: 100 });
    const isProgrammaticZoomRef = useRef(false);

    // Calculate zoom range based on preset or days
    const calculateZoomRange = useCallback((preset, dataLength, days = null) => {
        if (!dataLength || dataLength < 2) return { start: 0, end: 100 };
        const nowIndex = dataLength - 1;
        let startIndex = 0;
        
        // If days is provided, use it directly
        if (days !== null && days > 0) {
            startIndex = Math.max(0, nowIndex - days);
        } else {
            // Otherwise use preset-based calculation
            switch (preset) {
                case '3Y':
                    startIndex = Math.max(0, nowIndex - 756);
                    break;
                case '2Y':
                    startIndex = Math.max(0, nowIndex - 504);
                    break;
                case '1Y':
                    startIndex = Math.max(0, nowIndex - 252);
                    break;
                case '6M':
                    startIndex = Math.max(0, nowIndex - 126);
                    break;
                case '3M':
                    startIndex = Math.max(0, nowIndex - 63);
                    break;
                case 'Now':
                default:
                    startIndex = Math.max(0, nowIndex - 63); // Default to 3M view
                    break;
            }
        }
        
        const startPercent = nowIndex > 0 ? (startIndex / nowIndex) * 100 : 0;
        const endPercent = 100;
        
        return { start: Math.max(0, Math.min(100, startPercent)), end: endPercent };
    }, []);
    


    const initializeChart = useCallback((riskParam, timelineDataParam) => {
        const chartDom = chartRef.current;
        const currentRisk = riskParam; // Use parameter instead of closure
        const currentTimelineData = timelineDataParam; // Use parameter instead of closure
        
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:983',message:'ExposureTimelineChart initializeChart called',data:{riskParam:riskParam,currentRisk:currentRisk,timelineDataLength:currentTimelineData?.length,firstDate:currentTimelineData?.[0]?.date,lastDate:currentTimelineData?.[currentTimelineData?.length - 1]?.date},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'N'})}).catch(()=>{});
        // #endregion
        
        if (!chartDom || !currentTimelineData || currentTimelineData.length === 0) {
            console.log(`[Chart] Chart initialization skipped: no timeline data (length: ${currentTimelineData?.length || 0})`);
            return () => {}; // Return empty cleanup function
        }

        console.log(`[Chart] Initializing chart with ${currentTimelineData.length} data points for risk profile: ${currentRisk}`);
        
        // Validate data frequency before processing
        if (currentTimelineData.length >= 2) {
            const firstDate = new Date(currentTimelineData[0].date);
            const secondDate = new Date(currentTimelineData[1].date);
            const daysDiff = Math.round((secondDate - firstDate) / (1000 * 60 * 60 * 24));
            
            if (daysDiff > 1) {
                console.error(`[Chart] ERROR: Received ${daysDiff}-day intervals instead of daily data!`);
                console.error(`[Chart] First two dates: ${currentTimelineData[0].date} -> ${currentTimelineData[1].date}`);
            } else {
                console.log(`[Chart] ✓ Confirmed daily data: ${daysDiff}-day intervals`);
            }
        }

        // ALWAYS dispose any existing chart instance before creating a new one
        // This ensures clean state when risk or timelineData changes
        if (chartInstanceRef.current) {
            try {
                chartInstanceRef.current.dispose();
            } catch (e) {
                // Instance might already be disposed
            }
            chartInstanceRef.current = null;
        }
        
        const existingInstance = echarts.getInstanceByDom(chartDom);
        if (existingInstance) {
            try {
                existingInstance.dispose();
            } catch (e) {
                // Instance might already be disposed
            }
        }
        
        // Create fresh chart instance
        const chartInstance = echarts.init(chartDom);
        chartInstanceRef.current = chartInstance;

        // Get risk profile color using captured currentRisk value
        const scatterColorVar = currentRisk === 'Averse' ? '--handle-averse-color' : 
                               currentRisk === 'Neutral' ? '--handle-neutral-color' : 
                               '--handle-seeker-color';
        const lineColor = getCssVariable(scatterColorVar);
        const textColor = getCssVariable('--text-light');
        const gridColor = getCssVariable('--chart-panel-bg');
        const axisColor = '#4a4a4a';

        // Prepare data arrays - ensure ALL daily observations are included
        // Map ALL timeline data points without any filtering or reduction
        console.log(`[Chart] Mapping ${currentTimelineData.length} data points to chart arrays for risk: ${currentRisk}`);
        // #region agent log
        const lastItemInTimeline = currentTimelineData.length > 0 ? currentTimelineData[currentTimelineData.length - 1] : null;
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1048',message:'Chart: Last item in timelineData prop before mapping','data':{timelineLength:currentTimelineData.length,lastItem:lastItemInTimeline},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
        // #endregion
        const dates = currentTimelineData.map(d => d.date);
        const spyExposure = currentTimelineData.map(d => d.spyExposure || 0);
        const leveragedExposure = currentTimelineData.map(d => d.leveragedExposure || 0);
        const riskFreeExposure = currentTimelineData.map(d => d.riskFreeExposure !== undefined ? d.riskFreeExposure : (100 - (d.spyExposure || 0) - (d.leveragedExposure || 0)));
        
        // #region agent log
        const lastSpyExp = spyExposure.length > 0 ? spyExposure[spyExposure.length - 1] : null;
        const lastLeveragedExp = leveragedExposure.length > 0 ? leveragedExposure[leveragedExposure.length - 1] : null;
        const lastRiskFreeExp = riskFreeExposure.length > 0 ? riskFreeExposure[riskFreeExposure.length - 1] : null;
        const lastDate = dates.length > 0 ? dates[dates.length - 1] : null;
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1054',message:'Chart: Last observation values in mapped arrays','data':{lastDate:lastDate,lastSpyExposure:lastSpyExp,lastLeveragedExposure:lastLeveragedExp,lastRiskFreeExposure:lastRiskFreeExp,correspondingTimelineItem:lastItemInTimeline},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
        // #endregion
        
        console.log(`[Chart] Data arrays prepared:`, {
            datesLength: dates.length,
            spyExposureLength: spyExposure.length,
            leveragedExposureLength: leveragedExposure.length,
            riskFreeExposureLength: riskFreeExposure.length,
            firstDate: dates[0],
            lastDate: dates[dates.length - 1]
        });

        // Get color variables for each component
        const spyColor = getCssVariable('--asset-spx'); // Blue
        const tqqqColor = '#FF8C00'; // Orange for Leveraged ETF
        const cashColor = '#4CAF50'; // Green for Risk Free

        // Calculate time markers (3Y, 2Y, 1Y, 6M, 3M, Now) for x-axis labels
        const nowIndex = dates.length - 1;
        const markers = [
            { name: '3Y', position: Math.max(0, nowIndex - 756) },
            { name: '2Y', position: Math.max(0, nowIndex - 504) },
            { name: '1Y', position: Math.max(0, nowIndex - 252) },
            { name: '6M', position: Math.max(0, nowIndex - 126) },
            { name: '3M', position: Math.max(0, nowIndex - 63) },
            { name: 'Now', position: nowIndex }
        ];

        // Use current zoom range from ref
        const currentZoomRange = zoomRangeRef.current;

        const option = {
            backgroundColor: 'transparent',
            color: [spyColor, tqqqColor, cashColor], // Explicit colors for legend: Blue, Orange, Green
            grid: {
                top: '18%',
                left: '5%',
                right: '5%',
                bottom: '20%',
                containLabel: true
            },
            dataZoom: [
                {
                    type: 'inside',
                    start: currentZoomRange.start,
                    end: currentZoomRange.end,
                    zoomOnMouseWheel: true,
                    moveOnMouseMove: true,
                    moveOnMouseWheel: false
                },
                {
                    type: 'slider',
                    start: currentZoomRange.start,
                    end: currentZoomRange.end,
                    top: '3%',
                    height: 30,
                    left: '20%', // Narrower slider, centered with more padding for edge labels
                    right: '20%', // Narrower slider, centered with more padding for edge labels
                    handleStyle: {
                        color: lineColor,
                        borderColor: lineColor
                    },
                    dataBackground: {
                        lineStyle: {
                            color: axisColor,
                            opacity: 0.3
                        },
                        areaStyle: {
                            color: axisColor,
                            opacity: 0.1
                        }
                    },
                    selectedDataBackground: {
                        lineStyle: {
                            color: lineColor,
                            opacity: 0.8
                        },
                        areaStyle: {
                            color: lineColor,
                            opacity: 0.2
                        }
                    },
                    borderColor: axisColor,
                    textStyle: {
                        color: textColor,
                        fontSize: 10
                    },
                    filterMode: 'filter', // 'filter' ensures all data points are kept in memory, just filtered by zoom
                    realtime: true // Update chart in real-time during zoom
                }
            ],
            tooltip: {
                trigger: 'axis',
                backgroundColor: 'rgba(46, 50, 56, 0.95)',
                borderColor: 'transparent',
                textStyle: { color: '#fff', fontSize: 12 },
                padding: 12,
                // Ensure tooltip shows all daily data points
                axisPointer: {
                    type: 'line',
                    snap: true, // Snap to nearest data point (daily)
                    label: {
                        show: true,
                        precision: 0,
                        formatter: function(params) {
                            // Show the exact date for the data point
                            return params.value;
                        }
                    }
                },
                formatter: function(params) {
                    // params[0].dataIndex gives us the exact index in the daily data array
                    const dataIndex = params[0].dataIndex;
                    if (dataIndex < 0 || dataIndex >= currentTimelineData.length) {
                        console.error(`[Chart] Tooltip error: dataIndex ${dataIndex} out of range (0-${currentTimelineData.length - 1})`);
                        return '';
                    }
                    const point = currentTimelineData[dataIndex];
                    const fullExp = (point.spyExposure || 0) + (point.leveragedExposure || 0);
                    const riskFree = point.riskFreeExposure !== undefined ? point.riskFreeExposure : (100 - fullExp);
                    
                    // Show the exact date from the data point
                    const dateLabel = params[0].axisValue || point.date || 'Unknown date';
                    
                    // #region agent log
                    const isLastPoint = dataIndex === currentTimelineData.length - 1;
                    if (isLastPoint) {
                        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1161',message:'Chart tooltip: Last observation data displayed','data':{dataIndex:dataIndex,point:point,dateLabel:dateLabel,fullExp:fullExp,riskFree:riskFree,isLastPoint:true},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
                    }
                    // #endregion
                    
                    return `
                        <div style="font-weight: bold; margin-bottom: 8px; color: ${textColor};">${dateLabel}</div>
                        <div style="margin: 4px 0;">
                            <span style="color: ${spyColor}; margin-right: 8px;">●</span>
                            <span style="color: #ccc;">SPY Correlated ETF:</span> 
                            <span style="font-weight: bold; color: ${spyColor};">${(point.spyExposure || 0).toFixed(1)}%</span>
                        </div>
                        <div style="margin: 4px 0;">
                            <span style="color: ${tqqqColor}; margin-right: 8px;">●</span>
                            <span style="color: #ccc;">Leveraged ETF:</span> 
                            <span style="font-weight: bold; color: ${tqqqColor};">${(point.leveragedExposure || 0).toFixed(1)}%</span>
                        </div>
                        <div style="margin: 4px 0;">
                            <span style="color: ${cashColor}; margin-right: 8px;">●</span>
                            <span style="color: #ccc;">Risk Free:</span> 
                            <span style="font-weight: bold; color: ${cashColor};">${riskFree.toFixed(1)}%</span>
                        </div>
                        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.1);">
                            <span style="color: #ccc;">Total Exposure:</span> 
                            <span style="font-weight: bold; color: ${textColor};">${fullExp.toFixed(1)}%</span>
                        </div>
                    `;
                }
            },
            legend: {
                data: ['SPY Correlated ETF', 'Leveraged ETF', 'Risk Free'],
                bottom: '15%',
                textStyle: { color: textColor, fontSize: 11 },
                itemWidth: 12,
                itemHeight: 8,
                itemGap: 15
            },
            xAxis: {
                type: 'category',
                data: dates, // All daily dates - no filtering or reduction
                boundaryGap: false,
                axisLine: { lineStyle: { color: axisColor } },
                axisTick: { show: false },
                axisLabel: { 
                    color: textColor,
                    fontSize: 10,
                    // Show all dates in the axis data, but only display marker labels to avoid clutter
                    // All dates are in the data array for proper daily granularity
                    formatter: function(value, index) {
                        // Show only marker labels
                        const marker = markers.find(m => dates[m.position] === value);
                        return marker ? marker.name : '';
                    },
                    // Ensure all data points are available for tooltip/hover
                    showMinLabel: true,
                    showMaxLabel: true
                },
                splitLine: { show: false },
                // Ensure no data reduction happens at axis level
                scale: false
            },
            yAxis: {
                type: 'value',
                name: '', // Removed "Exposure %" label to avoid overlap with zoom slider
                min: 0,
                max: 100,
                interval: 25,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { 
                    color: textColor,
                    fontSize: 10,
                    formatter: '{value}%'
                },
                splitLine: { 
                    lineStyle: { 
                        color: '#3b3b3b',
                        type: 'dashed',
                        opacity: 0.5
                    }
                }
            },
            series: [
                {
                    name: 'SPY Correlated ETF',
                    type: 'line',
                    stack: 'exposure',
                    data: spyExposure,
                    large: true, // Enable large dataset optimization while keeping all points
                    largeThreshold: 2000, // Only optimize if more than 2000 points
                    smooth: true,
                    symbol: 'none',
                    sampling: 'none', // Disable sampling to show all daily observations
                    lineStyle: { width: 0 },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: spyColor + 'CC' },
                            { offset: 1, color: spyColor + '40' }
                        ])
                    },
                    emphasis: {
                        focus: 'series',
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: spyColor },
                                { offset: 1, color: spyColor + '80' }
                            ])
                        }
                    }
                },
                {
                    name: 'Leveraged ETF',
                    type: 'line',
                    stack: 'exposure',
                    data: leveragedExposure,
                    large: true,
                    largeThreshold: 2000,
                    smooth: true,
                    symbol: 'none',
                    sampling: 'none', // Disable sampling to show all daily observations
                    lineStyle: { width: 0 },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: tqqqColor + 'CC' },
                            { offset: 1, color: tqqqColor + '40' }
                        ])
                    },
                    emphasis: {
                        focus: 'series',
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: tqqqColor },
                                { offset: 1, color: tqqqColor + '80' }
                            ])
                        }
                    }
                },
                {
                    name: 'Risk Free',
                    type: 'line',
                    stack: 'exposure',
                    data: riskFreeExposure,
                    large: true,
                    largeThreshold: 2000,
                    smooth: true,
                    symbol: 'none',
                    sampling: 'none', // Disable sampling to show all daily observations
                    lineStyle: { width: 0 },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: cashColor + 'CC' },
                            { offset: 1, color: cashColor + '40' }
                        ])
                    },
                    emphasis: {
                        focus: 'series',
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: cashColor },
                                { offset: 1, color: cashColor + '80' }
                            ])
                        }
                    }
                }
            ]
        };

        chartInstance.setOption(option, true);
        
        // Verify chart data was set correctly
        const chartOption = chartInstance.getOption();
        const seriesData = chartOption.series;
        if (seriesData && seriesData.length > 0) {
            const firstSeriesDataLength = seriesData[0].data?.length || 0;
            console.log(`[Chart] Chart initialized with ${firstSeriesDataLength} data points in series for risk: ${currentRisk}`);
            if (firstSeriesDataLength !== currentTimelineData.length) {
                console.error(`[Chart] ERROR: Data mismatch! Timeline has ${currentTimelineData.length} points but chart series has ${firstSeriesDataLength} for risk: ${currentRisk}`);
            } else {
                console.log(`[Chart] ✓ Data integrity verified: all ${currentTimelineData.length} points are in chart for risk: ${currentRisk}`);
            }
        }

        // Listen for dataZoom events to update state
        chartInstance.on('datazoom', (params) => {
            let newStart = zoomRangeRef.current.start;
            let newEnd = zoomRangeRef.current.end;
            
            if (params.batch && params.batch.length > 0) {
                const zoomData = params.batch[0];
                newStart = zoomData.start;
                newEnd = zoomData.end;
            } else if (params.start !== undefined && params.end !== undefined) {
                newStart = params.start;
                newEnd = params.end;
            }
            
            zoomRangeRef.current = { start: newStart, end: newEnd };
            setZoomRange({ start: newStart, end: newEnd });
        });

        const handleResize = () => {
            if (chartInstance && !chartInstance.isDisposed()) {
                chartInstance.resize();
            }
        };

        window.addEventListener('resize', handleResize);

        // Return cleanup function
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [chartRef, getCssVariable]);
    
    // Merge currentData with timelineData: replace last observation if dates match, otherwise append
    const mergedTimelineData = useMemo(() => {
        if (!timelineData || timelineData.length === 0) {
            return currentData ? [currentData] : [];
        }
        if (!currentData) {
            return timelineData;
        }
        
        // Check if currentData date matches the last timeline item
        const lastTimelineItem = timelineData[timelineData.length - 1];
        const datesMatch = lastTimelineItem && lastTimelineItem.date === currentData.date;
        const valuesDiffer = datesMatch && (
            (lastTimelineItem.spyExposure !== currentData.spyExposure) ||
            (lastTimelineItem.leveragedExposure !== currentData.leveragedExposure) ||
            (lastTimelineItem.riskFreeExposure !== currentData.riskFreeExposure)
        );
        
        // #region agent log - Track data around 2025-12-11 for Seeker in frontend
        if (risk === "Seeker" && timelineData.length > 0) {
            const targetDateStr = "2025-12-11";
            const targetDate = new Date(targetDateStr);
            // Find items around 2025-12-11
            const itemsAround = [];
            for (let i = 0; i < timelineData.length; i++) {
                try {
                    const itemDate = new Date(timelineData[i].date);
                    const daysDiff = Math.round((itemDate - targetDate) / (1000 * 60 * 60 * 24));
                    if (-5 <= daysDiff && daysDiff <= 10) {
                        itemsAround.push({index: i, daysDiff, ...timelineData[i]});
                    }
                } catch (e) {
                    // Skip invalid dates
                }
            }
            if (itemsAround.length > 0) {
                fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1404',message:'Frontend: Timeline data around 2025-12-11 for Seeker before merge','data':{risk:risk,timelineLength:timelineData.length,itemsAround:itemsAround,currentData:currentData},timestamp:Date.now(),sessionId:'debug-session',runId:'seeker-date-debug',hypothesisId:'SEEKER_FRONTEND'})}).catch(()=>{});
            }
        }
        // #endregion
        
        // #region agent log
        if (datesMatch && valuesDiffer) {
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1414',message:'Fix: Merging currentData to replace last timeline item (dates match but values differ)','data':{risk:risk,lastTimelineItem:lastTimelineItem,currentData:currentData,replacing:true},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'FIX'})}).catch(()=>{});
        }
        // #endregion
        
        const merged = datesMatch 
            ? [...timelineData.slice(0, -1), currentData]
            : [...timelineData, currentData];
        
        // #region agent log - Track merged data around 2025-12-11 for Seeker
        if (risk === "Seeker" && merged.length > 0) {
            const targetDateStr = "2025-12-11";
            const targetDate = new Date(targetDateStr);
            const itemsAfterMerge = [];
            for (let i = 0; i < merged.length; i++) {
                try {
                    const itemDate = new Date(merged[i].date);
                    const daysDiff = Math.round((itemDate - targetDate) / (1000 * 60 * 60 * 24));
                    if (-5 <= daysDiff && daysDiff <= 10) {
                        itemsAfterMerge.push({index: i, daysDiff, ...merged[i]});
                    }
                } catch (e) {
                    // Skip invalid dates
                }
            }
            if (itemsAfterMerge.length > 0) {
                fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1440',message:'Frontend: Timeline data around 2025-12-11 for Seeker after merge','data':{risk:risk,mergedLength:merged.length,itemsAfterMerge:itemsAfterMerge},timestamp:Date.now(),sessionId:'debug-session',runId:'seeker-date-debug',hypothesisId:'SEEKER_FRONTEND'})}).catch(()=>{});
            }
        }
        // #endregion
        
        return merged;
    }, [timelineData, currentData, risk]);
    
    // Function to set zoom by days (exposed via ref) - defined after mergedTimelineData
    const setZoomByDays = useCallback((days) => {
        if (!mergedTimelineData || mergedTimelineData.length === 0) return;
        const zoomRange = calculateZoomRange(null, mergedTimelineData.length, days);
        if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
            isProgrammaticZoomRef.current = true;
            chartInstanceRef.current.dispatchAction({
                type: 'dataZoom',
                dataZoomIndex: 0,
                start: zoomRange.start,
                end: zoomRange.end
            });
            chartInstanceRef.current.dispatchAction({
                type: 'dataZoom',
                dataZoomIndex: 1,
                start: zoomRange.start,
                end: zoomRange.end
            });
            setZoomRange(zoomRange);
            setTimeout(() => {
                isProgrammaticZoomRef.current = false;
            }, 100);
        }
    }, [mergedTimelineData, calculateZoomRange]);
    
    // Expose setZoomByDays via ref
    useEffect(() => {
        if (zoomControlRef) {
            zoomControlRef.current = { setZoomByDays };
        }
    }, [zoomControlRef, setZoomByDays]);
    
    // useEffect to call initializeChart when dependencies change
    useEffect(() => {
        initializeChart(risk, mergedTimelineData);
    }, [mergedTimelineData, risk, initializeChart]);
    
    // Separate cleanup effect that only runs on unmount
    useEffect(() => {
        return () => {
            // This cleanup only runs when component unmounts
            if (chartInstanceRef.current) {
                try {
                    chartInstanceRef.current.off('datazoom');
                    if (!chartInstanceRef.current.isDisposed()) {
                        chartInstanceRef.current.dispose();
                    }
                } catch (e) {
                    // Chart might already be disposed
                }
                chartInstanceRef.current = null;
            }
        };
    }, []); // Empty deps = only on unmount

    useEffect(() => {
        // Initialize zoom range on first load
        if (mergedTimelineData && mergedTimelineData.length > 0 && zoomRangeRef.current.start === 0 && zoomRangeRef.current.end === 100) {
            const initialRange = calculateZoomRange('3M', mergedTimelineData.length);
            zoomRangeRef.current = initialRange;
            setZoomRange(initialRange);
        }
    }, [mergedTimelineData, calculateZoomRange]);

    // Track data hash to detect actual data changes
    const previousDataHashRef = useRef(null);
    const initializationDoneRef = useRef(false);
    
    // Memoize data hash to prevent unnecessary re-renders
    const dataHash = useMemo(() => {
        if (!mergedTimelineData || mergedTimelineData.length === 0) return null;
        return `${mergedTimelineData.length}-${mergedTimelineData[0]?.date}-${mergedTimelineData[mergedTimelineData.length - 1]?.date}-${risk}`;
    }, [mergedTimelineData, risk]);
    
    useEffect(() => {
        // Only initialize if we have data
        if (!mergedTimelineData || mergedTimelineData.length === 0) {
            return;
        }
        
        const currentDataHash = dataHash;
        const previousHash = previousDataHashRef.current;
        
        // Skip if hash hasn't changed and chart already exists
        if (chartInstanceRef.current && currentDataHash === previousHash) {
            return;
        }
        
        // Only log if this is a new initialization, not an update
        if (!chartInstanceRef.current) {
            console.log(`[Chart] Creating new chart instance`);
        } else {
            console.log(`[Chart] Updating chart with new data (hash changed: ${previousHash} -> ${currentDataHash})`);
        }
        
        // Dispose existing chart before creating new one if data changed
        if (chartInstanceRef.current && currentDataHash !== previousHash) {
            try {
                if (!chartInstanceRef.current.isDisposed()) {
                    chartInstanceRef.current.dispose();
                }
            } catch (e) {
                // Ignore disposal errors
            }
            chartInstanceRef.current = null;
        }
        
        // Initialize/update chart with current risk and mergedTimelineData
        initializeChart(risk, mergedTimelineData);
        previousDataHashRef.current = currentDataHash;
        initializationDoneRef.current = true;
        
        // Apply initial zoom after chart is created (only on first init or data change)
        if (chartInstanceRef.current && mergedTimelineData && mergedTimelineData.length > 0) {
            const initialRange = calculateZoomRange('3M', mergedTimelineData.length);
            setTimeout(() => {
                if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
                    isProgrammaticZoomRef.current = true;
                    chartInstanceRef.current.dispatchAction({
                        type: 'dataZoom',
                        dataZoomIndex: 0,
                        start: initialRange.start,
                        end: initialRange.end
                    });
                    chartInstanceRef.current.dispatchAction({
                        type: 'dataZoom',
                        dataZoomIndex: 1,
                        start: initialRange.start,
                        end: initialRange.end
                    });
                    setTimeout(() => {
                        isProgrammaticZoomRef.current = false;
                    }, 100);
                }
            }, 100);
        }
        // Only depend on dataHash - initializeChart is stable enough
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataHash]);

    return (
        <div className="w-full h-full">
            <div ref={chartRef} className="w-full" style={{ minHeight: '350px', height: '100%' }}></div>
        </div>
    );
};

// --- Time Slider Component (Handles interaction and logic) ---
const DEFAULT_DATA_POINT = { position: 100, percent: 0, label: 'Now' };

const TimeSlider = ({ dataPoints, onDataPointChange }) => {
    const { t } = useTranslation();
    const degreeLabels = t('dashboard.opportunity.degrees', { returnObjects: true, defaultValue: {} }) || {};
    const nowLabel = t('dashboard.opportunity.nowLabel', 'Now');
    // Find initial data point for slider setup (Now: 100%)
    const initialDataPoint = (dataPoints && dataPoints.length > 0) 
        ? (dataPoints.find(p => p.position === 100) || dataPoints[dataPoints.length - 1])
        : DEFAULT_DATA_POINT;

    const [sliderValue, setSliderValue] = useState(initialDataPoint?.position || 100); 
    const [currentData, setCurrentData] = useState(initialDataPoint || DEFAULT_DATA_POINT);
    const sliderRef = useRef(null);

    // Determines the closest data point based on the slider value
    const getClosestDataPoint = useCallback((value) => {
        if (!dataPoints || dataPoints.length === 0) {
            return DEFAULT_DATA_POINT;
        }
        
        let closest = dataPoints[0];
        let minDiff = Infinity;

        dataPoints.forEach(point => {
            const diff = Math.abs((point.position || 0) - value);
            if (diff < minDiff) {
                minDiff = diff;
                closest = point;
            }
        });
        return closest;
    }, [dataPoints]);

    // Handle input change (live dragging)
    const handleSliderChange = (event) => {
        const value = Number(event.target.value);
        setSliderValue(value);
        const closest = getClosestDataPoint(value);
        // Only update the data point if it actually changed
        if (closest.percent !== currentData.percent) {
             setCurrentData(closest);
             onDataPointChange(closest.percent);
        }
    };
    
    // Handle change stop (snapping)
    const handleSliderMouseUp = () => {
        const closest = getClosestDataPoint(sliderValue);
        // Snap the slider value to the closest data point's exact position
        setSliderValue(closest.position);
        
        // Ensure the onDataPointChange is called with the final snapped value
        if (closest.percent !== currentData.percent) {
             setCurrentData(closest);
             onDataPointChange(closest.percent);
        } else {
             // Re-call just to ensure the ECharts animation runs if the user landed
             // exactly on the point but didn't trigger the change during drag
             onDataPointChange(closest.percent);
        }
    };

    // Calculate handle position relative to track width
    const handleStyle = {
        // 10px is half the handle width (20px) to center it correctly
        left: `calc(${sliderValue}% - 10px)` 
    };
    
    // Render the Opportunity Degree text
    const degreeText = getOpportunityDegree(currentData.percent, degreeLabels);

    return (
        <div className="w-full relative mt-10 max-w-sm mx-auto px-4"> 
            <div className="opportunity-slider-track">
                <input 
                    type="range"
                    min="0"
                    max="100"
                    step="0.01" 
                    value={sliderValue}
                    onChange={handleSliderChange}
                    onMouseUp={handleSliderMouseUp}
                    onTouchEnd={handleSliderMouseUp}
                    className="opportunity-slider-input"
                    ref={sliderRef}
                />
                
                {/* Custom Handle */}
                <div className="opportunity-handle" style={handleStyle}>
                    {/* Degree Label (Above Handle) */}
                    <div className="opportunity-label">
                        {degreeText}
                    </div>
                    {/* Timeframe Tooltip (Below Handle) */}
                    <div className="opportunity-tooltip">
                        {currentData.label === 'Now' ? nowLabel : currentData.label}
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Exposure Chart (Donut Chart ECharts) ---
const initializeExposureChart = (exposureRef, getCssVariable, context, risk, allocation) => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1708',message:'initializeExposureChart called',data:{risk:risk,allocation:allocation,allocationType:allocation?.constructor?.name,allocationLength:allocation?.length,allocationIsArray:Array.isArray(allocation),allocationSample:allocation?.slice?.(0,2)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
    // #endregion
    const chartDom = exposureRef.current;
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1712',message:'Chart DOM check',data:{hasChartDom:!!chartDom,chartDomType:chartDom?.constructor?.name,allocation:allocation,allocationIsNull:allocation===null,allocationIsUndefined:allocation===undefined},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
    // #endregion
    if (!chartDom) return;
    if (!allocation || !Array.isArray(allocation) || allocation.length === 0) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1715',message:'Invalid allocation data - returning early',data:{allocation:allocation,allocationType:allocation?.constructor?.name,allocationLength:allocation?.length,allocationIsArray:Array.isArray(allocation)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
        // #endregion
        return;
    }

    if (context.exposureChart) context.exposureChart.dispose();
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1718',message:'Before echarts.init',data:{hasContext:!!context,hasEcharts:typeof echarts !== 'undefined',hasInit:typeof echarts?.init === 'function',chartDomNodeName:chartDom?.nodeName},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
    // #endregion
    context.exposureChart = echarts.init(chartDom);
    
    const currentAllocation = allocation;
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1722',message:'Before mapping allocation data',data:{currentAllocationLength:currentAllocation?.length,firstAsset:currentAllocation?.[0],hasPercent:currentAllocation?.[0]?.percent !== undefined,hasTicker:currentAllocation?.[0]?.ticker !== undefined},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
    // #endregion
    // Map allocation data for ECharts
    const exposureData = currentAllocation.map(asset => ({
        value: asset.percent, 
        name: asset.ticker.split(' or ')[0] // Use first ticker for ECharts names (SPY, TQQQ, T-bill)
    }));

    // Calculate total stock exposure (SPY + TQQQ)
    const totalStockExposure = currentAllocation
        .filter(a => a.ticker !== 'T-bill or cash')
        .reduce((sum, item) => sum + item.percent, 0);

    const spxColor = getCssVariable('--asset-spx');
    const tqqqColor = getCssVariable('--asset-tqqq');
    const cashColor = getCssVariable('--asset-cash');
    const textColor = getCssVariable('--text-light');
    const chartBg = getCssVariable('--main-bg'); 

    const outerRadius = '75%'; 
    const innerRadius = '45%'; 
    const centerInnerRadius = '35%'; 
    const centerTextSize = 36; 
    const INNER_CIRCLE_NAME = 'Total Stock Exposure'; 

    const option = {
        backgroundColor: 'transparent',
        color: [spxColor, tqqqColor, cashColor], 
        title: { show: false },
        tooltip: {
            trigger: 'item',
            backgroundColor: 'rgba(46, 50, 56, 0.9)',
            textStyle: { color: textColor },
            borderColor: 'transparent',
            padding: 10,
            formatter: function (params) {
                if (params.seriesName === INNER_CIRCLE_NAME) {
                    return `<span style="font-weight: bold; color: ${textColor};">${params.seriesName}: ${totalStockExposure}%</span>`;
                }
                return `${params.name}: <span style="font-weight: bold; color: ${params.color};">${params.value}%</span>`;
            }
        },
        legend: {
            show: true,
            orient: 'horizontal',
            top: '2%', 
            left: 'center',
            itemGap: 10,
            textStyle: { color: textColor, fontSize: 12 },
            data: exposureData.map(d => d.name).concat(INNER_CIRCLE_NAME) 
        },
        series: [
            {
                name: 'Portfolio Weights',
                type: 'pie',
                radius: [innerRadius, outerRadius], 
                center: ['50%', '55%'], 
                data: exposureData,
                label: { show: false },
                labelLine: { show: false },
                emphasis: { scaleSize: 8, label: { show: false } },
                itemStyle: { borderColor: chartBg, borderWidth: 2 }
            },
            {
                name: INNER_CIRCLE_NAME, 
                type: 'pie',
                radius: ['0%', centerInnerRadius], 
                center: ['50%', '55%'], 
                data: [
                    { value: 100, name: INNER_CIRCLE_NAME } 
                ],
                label: {
                    position: 'center',
                    formatter: '{b|' + totalStockExposure + '%}',
                    fontSize: centerTextSize, 
                    rich: {
                        b: { fontSize: centerTextSize, fontWeight: 'bold', color: textColor }
                    }
                },
                itemStyle: { color: getCssVariable('--chart-panel-bg') }, // Use chart panel background for center fill
                z: 10
            }
        ]
    };

    context.exposureChart.setOption(option);
};

// --- Scatter Correlation Chart (Module 3) ---
const initializeScatterChart = (scatterRef, getCssVariable, risk, context, correlationData) => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1803',message:'initializeScatterChart called',data:{risk:risk,correlationData:correlationData,scatterDataLength:correlationData?.scatterData?.length,scatterDataSample:correlationData?.scatterData?.slice(0,3),m:correlationData?.m},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
    // #endregion
    const chartDom = scatterRef.current;
    if (!chartDom) return;

    if (context.scatterChart) context.scatterChart.dispose();
    
    context.scatterChart = echarts.init(chartDom);

    // Use API correlation data
    const { scatterData, lineData, m: beta } = correlationData || { scatterData: [], lineData: [], m: "0" };
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1812',message:'Scatter chart data extracted',data:{risk:risk,scatterDataLength:scatterData?.length,scatterDataSample:scatterData?.slice(0,3),beta:beta},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
    // #endregion

    // Get scatter color based on risk profile
    const scatterColorVar = risk === 'Averse' ? '--handle-averse-color' : 
                           risk === 'Neutral' ? '--handle-neutral-color' : 
                           '--handle-seeker-color';
    const scatterColor = getCssVariable(scatterColorVar);
    const textColor = getCssVariable('--text-light');
    const gridColor = '#3b3b3b'; 

    const option = {
        backgroundColor: 'transparent',
        grid: { top: '15%', left: '10%', right: '5%', bottom: '15%', containLabel: false },
        tooltip: {
            trigger: 'axis', // Change to axis trigger to support both scatter and line
            axisPointer: { type: 'cross', label: { backgroundColor: '#4a4a4a' } },
            backgroundColor: 'rgba(46, 50, 56, 0.9)',
            textStyle: { color: textColor, fontSize: 12 },
            borderColor: 'transparent',
            padding: 10,
            formatter: function (params) {
                let tooltipContent = '';
                params.forEach(param => {
                    // Only show tooltip for scatter points (Series 'Correlation')
                    if (param.seriesName === 'Correlation') {
                        tooltipContent = `
                            <span style="font-weight: bold; color: ${scatterColor}">Daily Observation</span><br/>
                            SPY Return: ${param.value[0]}%<br/>
                            Native Return: ${param.value[1]}%
                        `;
                    }
                });
                return tooltipContent; // Only return content if it's a scatter point
            }
        },
        title: {
            text: `Beta: ${beta}`,
            left: 'center',
            top: 5,
            textStyle: {
                color: scatterColor,
                fontSize: 16,
                fontWeight: 'bold'
            }
        },
        xAxis: {
            type: 'value', 
            name: 'SPY Daily Return (%)',
            nameLocation: 'middle',
            nameTextStyle: { color: '#ccc', padding: [15, 0, 0, 0] },
            min: -5, 
            max: 5, 
            axisLine: { lineStyle: { color: gridColor } },
            splitLine: { lineStyle: { color: gridColor, opacity: 0.5 } },
            axisLabel: { color: '#ccc' },
            axisTick: { show: false }
        },
        yAxis: {
            type: 'value',
            name: 'Native Portfolio Daily Return (%)',
            nameLocation: 'middle',
            nameTextStyle: { color: '#ccc', padding: [0, 0, 15, 0] },
            min: -5,
            max: 5, 
            axisLine: { lineStyle: { color: gridColor } },
            splitLine: { lineStyle: { color: gridColor, opacity: 0.5 } },
            axisLabel: { color: '#ccc' },
            axisTick: { show: false }
        },
        series: [
            {
                name: 'Correlation',
                type: 'scatter',
                data: scatterData,
                symbolSize: 8,
                itemStyle: {
                    color: scatterColor, 
                    opacity: 0.7, 
                    borderWidth: 1,
                    borderColor: scatterColor, 
                },
                emphasis: {
                    itemStyle: {
                        opacity: 1,
                        symbolSize: 12,
                        borderWidth: 2,
                    }
                },
                markLine: {
                    silent: true,
                    data: [
                        // Diagonal line y = x for reference
                        [{ xAxis: -5, yAxis: -5 }, { xAxis: 5, yAxis: 5 }]
                    ],
                    lineStyle: {
                        color: '#666',
                        type: 'dashed',
                        width: 1,
                        opacity: 0.5
                    },
                    symbol: ['none', 'none']
                },
            },
            // Regression Line Series
            {
                name: 'Regression Line',
                type: 'line',
                data: lineData,
                symbolSize: 0, // Hide dots
                smooth: false,
                lineStyle: {
                    color: scatterColor, // Same color as dots
                    width: 2,
                    type: 'solid'
                },
                tooltip: {
                    show: false // Hide tooltip for the line itself
                },
                z: 10 // Ensure line is above scatter points
            }
        ]
    };

    context.scatterChart.setOption(option);
    context.scatterChart.resize(); 
};


// --- NEW: MODULE 4 (RELATIVE PERFORMANCE CHART) COMPONENT ---
const RelativePerformanceChart = ({ risk, getCssVariable, isCssReady, chartContextRef, relativePerformanceData, chartTitles = {} }) => {
    const chart3mRef = useRef(null);
    const chart1yRef = useRef(null);
    const chart3yRef = useRef(null);
    // Use ref to store latest relativePerformanceData to avoid closure issues
    const relativePerformanceDataRef = useRef(relativePerformanceData);
    useEffect(() => {
        relativePerformanceDataRef.current = relativePerformanceData;
    }, [relativePerformanceData]);
    
    // Use ref to track the current risk being processed to prevent race conditions
    const currentRiskRef = useRef(risk);

    // The initialization logic will be in a useEffect
    useEffect(() => {
        if (!isCssReady || !chart3mRef.current || !chart1yRef.current || !chart3yRef.current) return;

        // Store current risk value to avoid closure issues and update ref
        const currentRisk = risk;
        currentRiskRef.current = risk;
        
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1843',message:'Component receiving risk prop and data',data:{risk:risk,currentRisk:currentRisk,relativePerformanceDataKeys:relativePerformanceData?Object.keys(relativePerformanceData):null,dataForRisk:relativePerformanceData?relativePerformanceData[currentRisk]:null},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
        // #endregion

        const context = chartContextRef.current;
        const scatterColorVar = currentRisk === 'Averse' ? '--handle-averse-color' : 
                               currentRisk === 'Neutral' ? '--handle-neutral-color' : 
                               '--handle-seeker-color';
        const nativeColor = getCssVariable(scatterColorVar);
        const spyColor = getCssVariable('--text-light');
        const bgColor = 'rgba(255, 255, 255, 0.05)';
        const splitLineColor = '#3b3b3b';

        // Helper to create the chart
        const createChart = (ref, chartKey, title, chartData) => {
            if (!ref) return;
            if (context[chartKey]) context[chartKey].dispose();
            
            // Dispose any existing ECharts instance on this DOM element
            const existingInstance = echarts.getInstanceByDom(ref);
            if (existingInstance) {
                existingInstance.dispose();
            }
            
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1876',message:'Inside createChart with chartData',data:{currentRisk:currentRisk,chartKey:chartKey,title:title,chartData:chartData},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'I'})}).catch(()=>{});
            // #endregion
            
            let chartInstance;
            try {
                 chartInstance = echarts.init(ref);
            } catch (e) {
                console.error("Failed to init chart", e);
                return;
            }

            // Extract values immediately to prevent closure issues
            const spyValue = Number(chartData?.SPY) || 0;
            const nativeValue = Number(chartData?.Native) || 0;
            
            const values = [spyValue, nativeValue].filter((value) => !Number.isNaN(value));
            
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:1888',message:'Values extracted from chartData',data:{currentRisk:currentRisk,chartKey:chartKey,spyValue:spyValue,nativeValue:nativeValue,values:values},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'I'})}).catch(()=>{});
            // #endregion

            const observedMin = values.length ? Math.min(...values) : 0;
            const observedMax = values.length ? Math.max(...values) : 0;
            const range = observedMax - observedMin;
            const padding = Math.max(1, Math.abs(range || observedMax || 1) * 0.1);

            let paddedMin = observedMin - padding;
            let paddedMax = observedMax + padding;

            if (paddedMin === paddedMax) {
                paddedMin -= 5;
                paddedMax += 5;
            }

            paddedMin = Math.floor(paddedMin);
            paddedMax = Math.ceil(paddedMax);
            
            const option = {
                backgroundColor: 'transparent',
                title: {
                    text: title,
                    left: 'center',
                    top: '5%',
                    textStyle: { color: '#ccc', fontSize: 14, fontWeight: 'normal' }
                },
                grid: { left: '5%', right: '5%', top: '30%', bottom: '5%', containLabel: true },
                tooltip: {
                    trigger: 'item',
                    backgroundColor: 'rgba(46, 50, 56, 0.9)',
                    textStyle: { color: '#fff' },
                    borderColor: 'transparent',
                    formatter: '{b}: <span style="font-weight:bold;">{c}%</span>'
                },
                xAxis: {
                    type: 'category',
                    data: ['SPY', 'Altradenative'],
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { color: '#ccc', fontSize: 12 }
                },
                yAxis: {
                    type: 'value',
                    min: paddedMin,
                    max: paddedMax,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                    splitLine: { show: true, lineStyle: { color: splitLineColor, type: 'dashed' } }
                },
                series: [{
                    name: title,
                    type: 'bar',
                    data: [
                        { 
                            value: spyValue, 
                            name: 'SPY', 
                            itemStyle: { color: spyColor, borderRadius: [4, 4, 0, 0] } 
                        },
                        { 
                            value: nativeValue, 
                            name: 'Altradenative', 
                            itemStyle: { color: nativeColor, borderRadius: [4, 4, 0, 0] } 
                        }
                    ],
                    barWidth: '50%',
                    showBackground: true,
                    backgroundStyle: { color: bgColor },
                    label: {
                        show: true,
                        position: 'top',
                        color: '#f0f0f0',
                        fontSize: 14,
                        fontWeight: 'bold',
                        formatter: '{c}%'
                    },
                    emphasis: {
                        focus: 'series',
                        itemStyle: {
                            shadowBlur: 10,
                            shadowOffsetX: 0,
                            shadowOffsetY: 0,
                            shadowColor: 'rgba(0, 0, 0, 0.5)'
                        }
                    }
                }]
            };
            chartInstance.setOption(option);
            context[chartKey] = chartInstance;
        };
        
        // Make chart keys unique per risk profile to prevent conflicts
        const chartKey3m = `chart3m_${currentRisk}`;
        const chartKey1y = `chart1y_${currentRisk}`;
        const chartKey3y = `chart3y_${currentRisk}`;
        
        // Dispose ANY existing chart instances on the DOM elements BEFORE creating new ones
        // This ensures clean state regardless of which profile they were for
        [chart3mRef.current, chart1yRef.current, chart3yRef.current].forEach(ref => {
            if (ref) {
                const existingInstance = echarts.getInstanceByDom(ref);
                if (existingInstance) {
                    existingInstance.dispose();
                }
            }
        });
        
        // Clean up chart instances stored in context for other risk profiles
        const allRiskProfiles = ['Averse', 'Neutral', 'Seeker'];
        allRiskProfiles.forEach(profile => {
            if (profile !== currentRisk) {
                const oldKey3m = `chart3m_${profile}`;
                const oldKey1y = `chart1y_${profile}`;
                const oldKey3y = `chart3y_${profile}`;
                if (context[oldKey3m]) { 
                    try { context[oldKey3m].dispose(); } catch(e) {}
                    context[oldKey3m] = null; 
                }
                if (context[oldKey1y]) { 
                    try { context[oldKey1y].dispose(); } catch(e) {}
                    context[oldKey1y] = null; 
                }
                if (context[oldKey3y]) { 
                    try { context[oldKey3y].dispose(); } catch(e) {}
                    context[oldKey3y] = null; 
                }
            }
        });
        
        // Also clean up charts for current risk profile if they exist (in case of re-render)
        if (context[chartKey3m]) { 
            try { context[chartKey3m].dispose(); } catch(e) {}
            context[chartKey3m] = null; 
        }
        if (context[chartKey1y]) { 
            try { context[chartKey1y].dispose(); } catch(e) {}
            context[chartKey1y] = null; 
        }
        if (context[chartKey3y]) { 
            try { context[chartKey3y].dispose(); } catch(e) {}
            context[chartKey3y] = null; 
        }
        
        const timer = setTimeout(() => {
            // Use ref to get the LATEST risk and data values, avoiding closure issues
            // Check if risk has changed since this timeout was scheduled - if so, abort
            const latestRisk = currentRiskRef.current;
            if (latestRisk !== currentRisk) {
                // Risk has changed, don't create charts with stale risk value
                return;
            }
            
            const latestData = relativePerformanceDataRef.current;
            
            // Extract data INSIDE the timeout callback using the LATEST data from ref
            // This prevents closure issues where an old data object might be captured
            const dataForCurrentRisk = latestData?.[currentRisk] ? {
                'Last 3 Months': { ...latestData[currentRisk]['Last 3 Months'] },
                'Last Year': { ...latestData[currentRisk]['Last Year'] },
                'Last 3 Years': { ...latestData[currentRisk]['Last 3 Years'] }
            } : {
                'Last 3 Months': { 'Native': 0, 'SPY': 0 },
                'Last Year': { 'Native': 0, 'SPY': 0 },
                'Last 3 Years': { 'Native': 0, 'SPY': 0 }
            };
            
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:2045',message:'About to create charts with data',data:{currentRisk:currentRisk,latestRisk:latestRisk,chartKey3m:chartKey3m,chartKey1y:chartKey1y,chartKey3y:chartKey3y,data3m:dataForCurrentRisk['Last 3 Months'],data1y:dataForCurrentRisk['Last Year'],data3y:dataForCurrentRisk['Last 3 Years']},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H'})}).catch(()=>{});
            // #endregion
             createChart(chart3mRef.current, chartKey3m, chartTitles.last3Months || 'Last 3 Months', dataForCurrentRisk['Last 3 Months']);
             createChart(chart1yRef.current, chartKey1y, chartTitles.lastYear || 'Last Year', dataForCurrentRisk['Last Year']);
             createChart(chart3yRef.current, chartKey3y, chartTitles.last3Years || 'Last 3 Years', dataForCurrentRisk['Last 3 Years']);
        }, 50); // Small delay to ensure refs are ready
        
        return () => {
            clearTimeout(timer);
            // Clean up charts on unmount or re-render using the keys for the current risk
            if (context[chartKey3m]) { context[chartKey3m].dispose(); context[chartKey3m] = null; }
            if (context[chartKey1y]) { context[chartKey1y].dispose(); context[chartKey1y] = null; }
            if (context[chartKey3y]) { context[chartKey3y].dispose(); context[chartKey3y] = null; }
        };

    }, [risk, isCssReady, getCssVariable, chartContextRef, relativePerformanceData]);

    return (
        <div className="flex flex-col md:flex-row gap-4 justify-between" style={{ minHeight: '250px' }}>
            <div ref={chart3mRef} className="w-full md:w-1/3 h-[250px]"></div>
            <div ref={chart1yRef} className="w-full md:w-1/3 h-[250px]"></div>
            <div ref={chart3yRef} className="w-full md:w-1/3 h-[250px]"></div>
        </div>
    );
};


// --- NEW: MODULE 5 (VOLATILITY CHART) COMPONENT ---
const VolatilityChart = ({ risk, getCssVariable, isCssReady, chartContextRef, volatilityData, chartTitles = {} }) => {
    const chartVol3mRef = useRef(null);
    const chartVol1yRef = useRef(null);
    const chartVol3yRef = useRef(null);

    // The initialization logic will be in a useEffect
    useEffect(() => {
        if (!isCssReady || !chartVol3mRef.current || !chartVol1yRef.current || !chartVol3yRef.current) return;

        const data = volatilityData[risk] || {
            'Last 3 Months': { 'Native': 0, 'SPY': 0 },
            'Last Year': { 'Native': 0, 'SPY': 0 },
            'Last 3 Years': { 'Native': 0, 'SPY': 0 }
        };
        const context = chartContextRef.current;
        const scatterColorVar = risk === 'Averse' ? '--handle-averse-color' : 
                               risk === 'Neutral' ? '--handle-neutral-color' : 
                               '--handle-seeker-color';
        const nativeColor = getCssVariable(scatterColorVar);
        const spyColor = '#E53E3E'; // Red color for SPY volatility
        const bgColor = 'rgba(255, 255, 255, 0.05)';
        const splitLineColor = '#3b3b3b';

        // Helper to create the chart
        const createChart = (ref, chartKey, title, chartData) => {
            if (!ref) return;
            if (context[chartKey]) context[chartKey].dispose();
            
            let chartInstance;
            try {
                 chartInstance = echarts.init(ref);
            } catch (e) {
                console.error("Failed to init chart", e);
                return;
            }

            // Calculate proper y-axis scale with padding
            const values = [
                Number(chartData?.SPY),
                Number(chartData?.Native)
            ].filter((value) => !Number.isNaN(value));

            const observedMin = values.length ? Math.min(...values) : 0;
            const observedMax = values.length ? Math.max(...values) : 0;
            const range = observedMax - observedMin;
            const padding = Math.max(1, Math.abs(range || observedMax || 1) * 0.1);

            let paddedMin = observedMin - padding;
            let paddedMax = observedMax + padding;

            // Ensure minimum range if values are the same
            if (paddedMin === paddedMax) {
                paddedMin -= 5;
                paddedMax += 5;
            }

            // Ensure minimum is at least 0 for volatility (can't be negative)
            paddedMin = Math.max(0, Math.floor(paddedMin));
            paddedMax = Math.ceil(paddedMax);
            
            const option = {
                backgroundColor: 'transparent',
                title: {
                    text: title,
                    left: 'center',
                    top: '5%',
                    textStyle: { color: '#ccc', fontSize: 14, fontWeight: 'normal' }
                },
                grid: { left: '5%', right: '5%', top: '30%', bottom: '5%', containLabel: true },
                tooltip: {
                    trigger: 'item',
                    backgroundColor: 'rgba(46, 50, 56, 0.9)',
                    textStyle: { color: '#fff' },
                    borderColor: 'transparent',
                    formatter: '{b}: <span style="font-weight:bold;">{c}%</span>'
                },
                xAxis: {
                    type: 'category',
                    data: ['SPY', 'Altradenative'],
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { color: '#ccc', fontSize: 12 }
                },
                yAxis: {
                    type: 'value',
                    min: paddedMin,
                    max: paddedMax,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                    splitLine: { show: true, lineStyle: { color: splitLineColor, type: 'dashed' } }
                },
                series: [{
                    name: title,
                    type: 'bar',
                    data: [
                        { 
                            value: chartData.SPY, 
                            name: 'SPY', 
                            itemStyle: { color: spyColor, borderRadius: [4, 4, 0, 0] } 
                        },
                        { 
                            value: chartData.Native, 
                            name: 'Altradenative', 
                            itemStyle: { color: nativeColor, borderRadius: [4, 4, 0, 0] } 
                        }
                    ],
                    barWidth: '50%',
                    showBackground: true,
                    backgroundStyle: { color: bgColor },
                    label: {
                        show: true,
                        position: 'top',
                        color: '#f0f0f0',
                        fontSize: 14,
                        fontWeight: 'bold',
                        formatter: '{c}%'
                    },
                    emphasis: {
                        focus: 'series',
                        itemStyle: {
                            shadowBlur: 10,
                            shadowOffsetX: 0,
                            shadowOffsetY: 0,
                            shadowColor: 'rgba(0, 0, 0, 0.5)'
                        }
                    }
                }]
            };
            chartInstance.setOption(option);
            context[chartKey] = chartInstance;
        };
        
        const timer = setTimeout(() => {
             createChart(chartVol3mRef.current, 'chartVol3m', chartTitles.last3Months || 'Last 3 Months', data['Last 3 Months']);
             createChart(chartVol1yRef.current, 'chartVol1y', chartTitles.lastYear || 'Last Year', data['Last Year']);
             createChart(chartVol3yRef.current, 'chartVol3y', chartTitles.last3Years || 'Last 3 Years', data['Last 3 Years']);
        }, 50); // Small delay to ensure refs are ready
       
        return () => {
            clearTimeout(timer);
            // Clean up charts on unmount or re-render
            if (context.chartVol3m) { context.chartVol3m.dispose(); context.chartVol3m = null; }
            if (context.chartVol1y) { context.chartVol1y.dispose(); context.chartVol1y = null; }
            if (context.chartVol3y) { context.chartVol3y.dispose(); context.chartVol3y = null; }
        };

    }, [risk, isCssReady, getCssVariable, chartContextRef, volatilityData]);

    return (
        <div className="flex flex-col md:flex-row gap-4 justify-between" style={{ minHeight: '250px' }}>
            <div ref={chartVol3mRef} className="w-full md:w-1/3 h-[250px]"></div>
            <div ref={chartVol1yRef} className="w-full md:w-1/3 h-[250px]"></div>
            <div ref={chartVol3yRef} className="w-full md:w-1/3 h-[250px]"></div>
        </div>
    );
};


// --- NEW: MODULE 6 (PORTFOLIO SUMMARY) COMPONENT ---
// --- Enhanced Portfolio Summary Component ---
const PortfolioSummary = ({ 
    risk, 
    currentAllocation, 
    performanceData, 
    volatilityData,
    marketStatus,
    quotes,
    correlationData,
    exposureTimelineData,
    performanceTimeSeries
}) => {
    const { t } = useTranslation();
    const summaryText = t('dashboard.summaryModule', { returnObjects: true, defaultValue: {} }) || {};
    const statusTitles = summaryText.statusTitles || {};
    const marketStatusLabels = summaryText.marketStatusLabels || {};
    const exposureTrendLabels = summaryText.exposureTrendLabels || {};
    const sectionsText = summaryText.sections || {};
    const metricsText = summaryText.metrics || {};
    const badgeTemplate = summaryText.badge || '{{profile}} Risk Profile';
    const performanceComparisonText = sectionsText.performance?.comparison || {};
    const betaInterpretationsText = sectionsText.risk?.betaInterpretations || {};
    const volComparisonTextMap = sectionsText.risk?.volComparison || {};
    const riskLabelMap = t('dashboard.riskProfiles.labels', { returnObjects: true, defaultValue: {} }) || {};

    const formatTemplate = (template, replacements) => {
        if (!template) return '';
        return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
            const value = replacements[key];
            return value === undefined || value === null ? '' : String(value);
        });
    };

    const getStatusTitle = (statusKey) => {
        if (statusKey === 'defensive') return statusTitles.defensive || 'Defensive Posture: Capital Preservation';
        if (statusKey === 'aggressive') return statusTitles.aggressive || 'Aggressive Posture: Alpha Generation';
        return statusTitles.balanced || 'Balanced Posture: Measured Growth';
    };

    const assessment = React.useMemo(() => {
        const leveragePercent = (currentAllocation || []).find(a => a.ticker && a.ticker.includes('TQQQ'))?.percent || 0;
        const riskFreePercent = (currentAllocation || []).find(a => a.ticker && a.ticker.includes('T-bill or cash'))?.percent || 0;
        const spyPercent = (currentAllocation || []).find(a => a.ticker === 'SPY' || a.ticker === 'SPY or VOO')?.percent || 0;

        const currentExposure = exposureTimelineData?.current?.fullExposure || 0;

        const defaultPerf = { Native: 0, SPY: 0 };
        const defaultVol = { Native: 0, SPY: 0 };

        const perf3m = performanceData?.[risk]?.['Last 3 Months'] || defaultPerf;
        const perf1y = performanceData?.[risk]?.['Last Year'] || defaultPerf;
        const perf3y = performanceData?.[risk]?.['Last 3 Years'] || defaultPerf;

        const vol3m = volatilityData?.[risk]?.['Last 3 Months'] || defaultVol;
        const vol1y = volatilityData?.[risk]?.['Last Year'] || defaultVol;
        const vol3y = volatilityData?.[risk]?.['Last 3 Years'] || defaultVol;

        const beta = parseFloat(correlationData?.m || '0');

        const perfComparisonKey = perf3y.Native > perf3y.SPY + 5 ? 'outperforming' : perf3y.Native < perf3y.SPY - 5 ? 'underperforming' : 'inline';
        const perfComparison = performanceComparisonText[perfComparisonKey] || (perfComparisonKey === 'inline' ? 'in line with' : perfComparisonKey);

        const volComparisonKey = vol3y.Native > vol3y.SPY + 2 ? 'higher' : vol3y.Native < vol3y.SPY - 2 ? 'lower' : 'similar';
        const volComparison = volComparisonTextMap[volComparisonKey] || volComparisonKey;

        let exposureTrend = 'stable';
        if (exposureTimelineData?.timeline && exposureTimelineData.timeline.length > 0) {
            const timeline = exposureTimelineData.timeline;
            const now = timeline[timeline.length - 1]?.fullExposure || 0;
            const oneMonthAgo = timeline[Math.max(0, timeline.length - 22)]?.fullExposure || 0;
            if (now > oneMonthAgo + 2) exposureTrend = 'increasing';
            else if (now < oneMonthAgo - 2) exposureTrend = 'decreasing';
        }
        const exposureTrendText = exposureTrendLabels[exposureTrend] || exposureTrend;

        const marketStatusKey = marketStatus?.isOpen ? 'open' : 'closed';
        const marketStatusTextValue = marketStatusLabels[marketStatusKey] || marketStatusKey;
        const spyQuote = quotes?.find(q => q.ticker === 'SPY');
        const spyChange = spyQuote ? parseFloat(spyQuote.change) : 0;

        const spyLine = spyQuote
            ? formatTemplate(sectionsText.marketContext?.spyLine, {
                price: spyQuote.price,
                change: `${spyChange >= 0 ? '+' : ''}${spyChange}`
            })
            : sectionsText.marketContext?.spyUnavailable || 'Market data is being updated. ';

        const marketContextContent = formatTemplate(sectionsText.marketContext?.content, {
            marketStatus: marketStatusTextValue,
            statusLabel: marketStatus?.statusLabel || 'Unknown',
            spyLine,
            currentExposure: currentExposure.toFixed(1),
            exposureTrend: exposureTrendText
        });

        const performanceContent = formatTemplate(sectionsText.performance?.content, {
            perf3mNative: perf3m.Native,
            perf3mSpy: perf3m.SPY,
            perf1yNative: perf1y.Native,
            perf1ySpy: perf1y.SPY,
            perf3yNative: perf3y.Native,
            perf3ySpy: perf3y.SPY,
            comparison: perfComparison
        });

        const betaInterpretationKey = beta < 1 ? 'lower' : beta > 1 ? 'higher' : 'similar';
        const betaInterpretation = betaInterpretationsText[betaInterpretationKey] || betaInterpretationKey;

        const riskContent = formatTemplate(sectionsText.risk?.content, {
            beta: beta.toFixed(2),
            betaInterpretation,
            vol3yNative: vol3y.Native,
            vol3ySpy: vol3y.SPY,
            volComparison,
            riskLabel: riskLabelMap[risk] || risk
        });

        let status = 'balanced';
        if (risk === 'Averse') status = 'defensive';
        if (risk === 'Seeker') status = 'aggressive';

        // Use currentExposure (fullExposure from timeline data) as equity markets exposure
        // This is the total equity exposure (SPY + Leveraged ETFs combined)
        const equityMarketsExposure = Math.round(currentExposure * 10) / 10; // Round to 1 decimal place

        const allocationTemplate =
            status === 'defensive'
                ? sectionsText.allocation?.defensive
                : status === 'aggressive'
                    ? sectionsText.allocation?.aggressive
                    : sectionsText.allocation?.balanced;

        const allocationContent = formatTemplate(allocationTemplate, {
            spyPercent,
            leveragePercent,
            riskFreePercent,
            equityMarketsExposure
        });

        return {
            title: getStatusTitle(status),
            status,
            sections: [
                {
                    type: 'market_context',
                    title: sectionsText.marketContext?.title || 'Current Market Environment',
                    content: marketContextContent
                },
                {
                    type: 'performance_breakdown',
                    title: sectionsText.performance?.title || 'Performance Analysis',
                    content: performanceContent
                },
                {
                    type: 'risk_metrics',
                    title: sectionsText.risk?.title || 'Risk Assessment',
                    content: riskContent
                },
                {
                    type: 'allocation_rationale',
                    title: sectionsText.allocation?.title || 'Portfolio Positioning',
                    content: allocationContent
                }
            ],
            metrics: {
                currentExposure,
                beta,
                perf3y,
                vol3y
            }
        };
    }, [
        risk,
        currentAllocation,
        performanceData,
        volatilityData,
        marketStatus,
        quotes,
        correlationData,
        exposureTimelineData,
        sectionsText,
        performanceComparisonText,
        betaInterpretationsText,
        volComparisonTextMap,
        riskLabelMap,
        marketStatusLabels,
        exposureTrendLabels,
        statusTitles
    ]);

    const colors = PORTFOLIO_SUMMARY_STATUS_COLORS[assessment.status] || PORTFOLIO_SUMMARY_STATUS_COLORS.balanced;
    const statusIcon = colors.icon;

    const createMarkup = (htmlString) => ({
        __html: htmlString.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
    });

    const formattedBadge = formatTemplate(badgeTemplate, { profile: riskLabelMap[risk] || risk });
    const currentExposureValue = assessment.metrics.currentExposure?.toFixed(1) || '0.0';
    const betaValue = assessment.metrics.beta !== undefined ? assessment.metrics.beta.toFixed(2) : '0.00';
    const perf3yNative = assessment.metrics.perf3y?.Native ?? 0;
    const perf3ySpy = assessment.metrics.perf3y?.SPY ?? 0;
    const vol3yNative = assessment.metrics.vol3y?.Native ?? 0;
    const vol3ySpy = assessment.metrics.vol3y?.SPY ?? 0;

    return (
        <div className="space-y-6">
            <div className="flex items-start gap-4 p-5 rounded-xl bg-gradient-to-r from-gray-800/50 to-gray-700/30 border border-gray-700">
                {statusIcon}
                <div className="flex-1">
                    <h4 className={`text-xl font-bold ${colors.color} mb-2`}>{assessment.title}</h4>
                    <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${colors.bgColor} ${colors.color} border ${colors.borderColor}`}>
                            {formattedBadge}
                        </span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {assessment.sections.map((section, index) => (
                    <div
                        key={section.type || index}
                        className="p-5 rounded-xl bg-gray-800/50 border border-gray-700 hover:border-gray-600 transition-all duration-300"
                    >
                        <h5 className="text-base font-semibold text-gray-200 mb-3 flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${colors.bgColor}`} style={{ opacity: 0.8 }}></span>
                            {section.title}
                        </h5>
                        <p
                            className="text-sm text-gray-300 leading-relaxed"
                            dangerouslySetInnerHTML={createMarkup(section.content)}
                        />
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-gray-800/30 border border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">{metricsText.currentExposure?.title || 'Current Exposure'}</p>
                    <p className="text-2xl font-bold text-white">{currentExposureValue}%</p>
                    <p className="text-xs text-gray-500 mt-1">{metricsText.currentExposure?.subtitle || 'Full Exposure'}</p>
                </div>
                <div className="p-4 rounded-lg bg-gray-800/30 border border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">{metricsText.portfolioBeta?.title || 'Portfolio Beta'}</p>
                    <p className="text-2xl font-bold text-white">{betaValue}</p>
                    <p className="text-xs text-gray-500 mt-1">{metricsText.portfolioBeta?.subtitle || 'vs Market (1.0)'}</p>
                </div>
                <div className="p-4 rounded-lg bg-gray-800/30 border border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">{metricsText.performance3y?.title || '3Y Performance'}</p>
                    <p className="text-2xl font-bold text-white">{perf3yNative}%</p>
                    <p className="text-xs text-gray-500 mt-1">{formatTemplate(metricsText.performance3y?.subtitle || 'vs SPY {{value}}%', { value: perf3ySpy })}</p>
                </div>
                <div className="p-4 rounded-lg bg-gray-800/30 border border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">{metricsText.volatility3y?.title || '3Y Volatility'}</p>
                    <p className="text-2xl font-bold text-white">{vol3yNative}%</p>
                    <p className="text-xs text-gray-500 mt-1">{formatTemplate(metricsText.volatility3y?.subtitle || 'vs SPY {{value}}%', { value: vol3ySpy })}</p>
                </div>
            </div>
        </div>
    );
};


// --- NEW: TUTORIAL TOGGLE SWITCH COMPONENT ---
const TutorialToggleSwitch = ({ enabled, setEnabled }) => {
  return (
    <button
      onClick={() => setEnabled(!enabled)}
      className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-700 focus:ring-blue-500 ${
        enabled ? 'bg-blue-500' : 'bg-gray-600'
      }`}
    >
      <span
        className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-300 ease-in-out ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
};

// --- NEW: TUTORIAL MODAL COMPONENT ---
const DashboardTutorial = ({ isOpen, onClose, showOnStartup, onToggleStartup }) => {
  const { t, i18n } = useTranslation();
  const tutorialText = t('dashboard.tutorial', { returnObjects: true, defaultValue: {} }) || {};
  const languageOptions = [
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' }
  ];
  const activeLanguage = (i18n.language || 'en').startsWith('es') ? 'es' : 'en';
  const languageNames = { en: 'English', es: 'Español' };
  const handleLanguageChange = (code) => {
    if (code !== activeLanguage) {
      i18n.changeLanguage(code);
    }
  };

  const [step, setStep] = useState(1);
  const [isClosing, setIsClosing] = useState(false); // New state for exit animation
  const totalSteps = 7;

  // Reset step to 1 when modal is opened
  useEffect(() => {
    if (isOpen && isClosing) setIsClosing(false); // Reset if opened again while closing
  }, [isOpen]);

  // New close handler to manage exit animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();      // Call parent's close function
      setIsClosing(false); // Reset for next time
    }, 300); // Must match animation duration
  };

  if (!isOpen && !isClosing) return null; // Keep component mounted for exit animation

  const defaultSteps = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg>
      ),
      title: 'What is Altradenative?',
      content: 'Altradenative is an online dashboard that makes investing in stocks simpler and more efficient, tailored to each user\'s risk profile.\n\nOur algorithm constantly monitors the U.S. market and builds a portfolio using only three types of assets:\n\n- A broad market index (S&P500 or Nasdaq100)\n\n- A leveraged version of that index (S&P500x3 or Nasdaq100x3)\n\n- A risk‑free asset (USD cash or U.S. Treasury Bonds)'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"></path><path d="m19 9-5 5-4-4-3 3"></path></svg>
      ),
      title: 'Past Performance',
      content: 'This model has been tested over 20 years:\n\n- For neutral or aggressive profiles, it has outperformed the S&P500\'s annual returns in more than 80% of cases, while keeping losses similar during major downturns.\n\n- For conservative profiles, it has reduced losses by over 50% in bear markets, while still capturing strong gains in bull markets.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
      ),
      title: 'A virtual analyst, not an advisor',
      content: 'It is important to note that Altradenative is an interactive virtual analyst: it provides information and education for investment decisions based on quantitative methods and statistical analysis. We do not recommend specific purchases, take orders, or manage investor funds.\n\n👉 Investing in stocks carries risk of loss, sometimes significant. For neutral and aggressive profiles, Altradenative\'s algorithm may also generate losses greater than those of global indices.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
      ),
      title: 'Choose Your Risk Profile',
      content: 'When you start, you\'ll pick one of three risk profiles: **Averse** (low risk), **Neutral** (medium risk), or **Seeker** (high risk). This choice helps the platform show you strategies that match how much risk you\'re comfortable taking.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"></path><path d="m19 9-5 5-4-4-3 3"></path></svg>
      ),
      title: 'Explore Real-Time and Historical Insights',
      content: 'The dashboard shows you: how the algorithm\'s portfolio is set up right now, and how it has performed over the past three years. This helps you see how the strategy works in different market conditions.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"></path></svg>
      ),
      title: 'Compare Portfolio and Benchmarks',
      content: 'You can examine the portfolio by looking at the types of assets it holds, how its performance compares to major market indexes such as the **S&P 500**, and key metrics like returns and risk. This helps highlight the strategy\'s strengths and weaknesses more clearly.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
      ),
      title: 'Review and Ask Questions',
      content: 'At the end, the dashboard gives you a summary of all the data. If you have questions or want more details, you can use the **virtual assistant** for help.'
    },
  ];

  const translatedSteps = Array.isArray(tutorialText.steps) ? tutorialText.steps : [];
  const tutorialSteps = defaultSteps.map((item, index) => ({
    ...item,
    title: translatedSteps[index]?.title || item.title,
    content: translatedSteps[index]?.content || item.content
  }));

  const previousLabel = tutorialText.previousAria || 'Previous step';
  const nextLabel = tutorialText.nextAria || 'Next step';
  const stepAriaTemplate = tutorialText.stepAria || 'Go to step {{step}}';
  const showOnStartupLabel = tutorialText.showOnStartup || 'Show on startup';
  const closeLabel = tutorialText.close || 'Close';

  const currentStepData = tutorialSteps[step - 1];
  
  // Use dangerouslySetInnerHTML to parse the **bold** tags with enhanced styling
  const createMarkup = (htmlString) => {
    let processed = htmlString
      // Replace **bold** with styled strong tags
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-bold bg-blue-900/20 px-1.5 py-0.5 rounded inline-block">$1</strong>')
      // Replace bullet points with styled bullets
      .replace(/^- /gm, '<span class="inline-block w-2 h-2 rounded-full bg-blue-500/60 mr-2 mt-1.5 align-middle"></span>');
    
    // Split by double newlines to create paragraphs
    const paragraphs = processed.split(/\n\n+/).filter(p => p.trim());
    const wrappedParagraphs = paragraphs.map(p => `<p class="mb-3">${p.trim()}</p>`).join('');
    
    return { __html: wrappedParagraphs || `<p>${processed}</p>` };
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Enhanced Backdrop with better blur */}
      <div 
        className={`fixed inset-0 bg-gradient-to-br from-black/80 via-black/70 to-black/80 backdrop-blur-md transition-opacity duration-300 ease-in-out ${isClosing ? 'opacity-0' : 'opacity-100'}`}
        onClick={handleClose}
      ></div>

      {/* Enhanced Modal Content with Glassmorphism */}
      <div className={`relative z-10 w-full max-w-md overflow-hidden rounded-xl bg-gradient-to-br from-[#2a3138]/95 via-[#2a3138]/98 to-[#2a3138]/95 backdrop-blur-xl shadow-2xl border border-gray-600/50 ${isClosing ? 'animate-modal-pop-out' : 'animate-modal-pop-in'}`}
           style={{ boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.05)' }}>
        
        {/* Enhanced Animated Step Content */}
        <div className="relative p-6 md:p-8 min-h-[240px] bg-gradient-to-b from-transparent via-transparent to-gray-900/20">
          <div className="absolute top-4 left-4">
            <div className="inline-flex rounded-full bg-gray-800/80 border border-gray-700/60 shadow-lg overflow-hidden backdrop-blur-sm">
              {languageOptions.map(({ code, label }) => (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code)}
                  className={`px-3 py-1 text-xs font-semibold transition-all duration-200 ${activeLanguage === code ? 'text-white shadow-md' : 'text-gray-300 hover:text-white hover:bg-gray-700/70'}`}
                  style={activeLanguage === code ? { backgroundColor: '#d2aa3a' } : {}}
                  aria-label={activeLanguage === code ? `${languageNames[code]} selected` : `Switch to ${languageNames[code]}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <img
            src={tutorialLogo}
            alt="Altradenative tutorial logo"
            className="absolute top-4 right-4 h-10 w-10 object-contain opacity-90"
          />
          {/* Enhanced content with better spacing and animations */}
          <div key={step} className="space-y-4 mt-8">
            {/* Enhanced Icon with animations */}
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blue-900/60 via-blue-800/50 to-blue-900/60 text-blue-300 mx-auto animate-icon-rotate shadow-lg border border-blue-700/30 hover:scale-105 transition-transform duration-300">
              <div className="animate-soft-pulse">
                {currentStepData.icon}
              </div>
            </div>
            {/* Enhanced Heading with Helvetica font */}
            <h2 className="text-lg font-semibold text-center text-white font-helvetica tracking-tight animate-fade-slide-up" 
                style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
              {currentStepData.title}
            </h2>
            {/* Enhanced Content with Helvetica font and better styling */}
            <div 
                className="text-sm text-gray-200 leading-relaxed animate-text-stagger font-helvetica text-justify"
                dangerouslySetInnerHTML={createMarkup(currentStepData.content)}
            >
            </div>
          </div>
        </div>
        
        {/* Enhanced Footer */}
        <div className="bg-gradient-to-t from-[#212529]/90 via-[#212529]/80 to-[#212529]/70 px-6 py-4 border-t border-gray-600/30 backdrop-blur-sm">
          
          {/* Enhanced Navigation & Step Dots */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setStep(s => Math.max(s - 1, 1))}
              disabled={step === 1}
              className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-gray-700/50 disabled:opacity-30 disabled:hover:text-gray-400 disabled:hover:bg-transparent transition-all duration-200"
              aria-label={previousLabel}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            </button>

            <div className="flex space-x-2">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i + 1)}
                  className={`h-2.5 w-2.5 rounded-full transition-all duration-300 ${step === i + 1 ? 'bg-blue-500 scale-125 shadow-lg shadow-blue-500/50' : 'bg-gray-600 hover:bg-gray-500 hover:scale-110'}`}
                  aria-label={stepAriaTemplate.replace('{{step}}', `${i + 1}`)}
                ></button>
              ))}
            </div>

            <button
              onClick={() => setStep(s => Math.min(s + 1, totalSteps))}
              disabled={step === totalSteps}
              className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-gray-700/50 disabled:opacity-30 disabled:hover:text-gray-400 disabled:hover:bg-transparent transition-all duration-200"
              aria-label={nextLabel}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </button>
          </div>
          
          <hr className="border-gray-600/30 mb-4" />

          {/* Bottom Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <TutorialToggleSwitch enabled={showOnStartup} setEnabled={onToggleStartup} />
              <label onClick={() => onToggleStartup(!showOnStartup)} className="text-xs text-gray-400 cursor-pointer hover:text-gray-200">
                {showOnStartupLabel}
              </label>
            </div>
            
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-md bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-semibold hover:from-blue-500 hover:to-blue-400 transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-blue-500"
            >
              {closeLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- NEW: LOADING SPINNER COMPONENT ---
const DashboardLoadingSpinner = ({ isVisible }) => {
    const { t } = useTranslation();
    const recalculatingText = t('dashboard.loading.recalculating', 'Recalculating...');
    const timeNoteText = t('dashboard.loading.timeNote', 'Algorithmic calculations can take some time');

    if (!isVisible) return null;
    
    return (
        <div className="dashboard-loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">{recalculatingText}</div>
            <div className="loading-note">{timeNoteText}</div>
        </div>
    );
};

// --- NEW: INVESTMENT ALLOCATION CALCULATOR ---

/**
 * Component to calculate and display investment allocation breakdown
 * Converts percentage allocations to dollar amounts and number of shares
 */
const InvestmentAllocationCalculator = ({ currentAllocation, quotes, getCssVariable, calculatorText = {} }) => {
    const { t, i18n } = useTranslation();
    const [investmentAmount, setInvestmentAmount] = useState(10000);
    const isSpanish = (i18n.language || 'en').startsWith('es');

    // Translation text with defaults - use calculatorText if available, otherwise use t() with fallbacks
    const title = calculatorText.title || t('dashboard.investmentCalculator.title', isSpanish ? 'Calculadora de Asignación de Inversión' : 'Investment Allocation Calculator');
    const description = calculatorText.description || t('dashboard.investmentCalculator.description', isSpanish ? 'Calcule cómo replicar la asignación algorítmica con un monto de inversión teórico' : 'Calculate how to replicate the algorithmic allocation with a theoretical investment amount');
    const theoreticalInvestment = calculatorText.theoreticalInvestment || t('dashboard.investmentCalculator.theoreticalInvestment', isSpanish ? 'Inversión Teórica:' : 'Theoretical Investment:');
    const multiplesHint = calculatorText.multiplesHint || t('dashboard.investmentCalculator.multiplesHint', isSpanish ? '(múltiplos de $1,000)' : '(multiples of $1,000)');
    const decreaseAria = calculatorText.decreaseAria || t('dashboard.investmentCalculator.decreaseAria', isSpanish ? 'Disminuir en $1,000' : 'Decrease by $1,000');
    const increaseAria = calculatorText.increaseAria || t('dashboard.investmentCalculator.increaseAria', isSpanish ? 'Aumentar en $1,000' : 'Increase by $1,000');
    const allocationLabel = calculatorText.allocationLabel || t('dashboard.investmentCalculator.allocationLabel', isSpanish ? 'Asignación' : 'Allocation');
    const sharesToPurchase = calculatorText.sharesToPurchase || t('dashboard.investmentCalculator.sharesToPurchase', isSpanish ? 'Acciones a Comprar' : 'Shares to Purchase');
    const share = calculatorText.share || t('dashboard.investmentCalculator.share', isSpanish ? 'acción' : 'share');
    const shares = calculatorText.shares || t('dashboard.investmentCalculator.shares', isSpanish ? 'acciones' : 'shares');
    const currentPrice = calculatorText.currentPrice || t('dashboard.investmentCalculator.currentPrice', isSpanish ? 'Precio Actual' : 'Current Price');
    const cashAllocation = calculatorText.cashAllocation || t('dashboard.investmentCalculator.cashAllocation', isSpanish ? 'Asignación de Efectivo / Letras del Tesoro' : 'Cash / T-bill Allocation');
    const cashAmountText = calculatorText.cashAmountText || t('dashboard.investmentCalculator.cashAmountText', isSpanish ? 'en efectivo o letras del Tesoro' : 'in cash or Treasury bills');
    const notAllocated = calculatorText.notAllocated || t('dashboard.investmentCalculator.notAllocated', isSpanish ? 'No asignado' : 'Not allocated');
    const priceUnavailable = calculatorText.priceUnavailable || t('dashboard.investmentCalculator.priceUnavailable', isSpanish ? 'Datos de precio no disponibles' : 'Price data unavailable');
    const minAmountMessage = calculatorText.minAmountMessage || t('dashboard.investmentCalculator.minAmountMessage', isSpanish ? 'Por favor ingrese un monto de inversión de al menos $1,000' : 'Please enter an investment amount of at least $1,000');
    const loadingMessage = calculatorText.loadingMessage || t('dashboard.investmentCalculator.loadingMessage', isSpanish ? 'Cargando datos de asignación...' : 'Loading allocation data...');
    const allocatedLabel = calculatorText.allocatedLabel || t('dashboard.investmentCalculator.allocatedLabel', isSpanish ? 'asignado' : 'allocated');

    // Helper to format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    // Get CSS variable value
    const getColor = (colorVar) => {
        if (typeof window !== 'undefined' && getCssVariable) {
            return getCssVariable(colorVar);
        }
        // Fallback colors
        if (colorVar === '--asset-spx') return '#3b82f6';
        if (colorVar === '--asset-tqqq') return '#ef4444';
        if (colorVar === '--asset-cash') return '#10b981';
        return '#6b7280';
    };

    // Calculate allocation breakdown
    const calculateAllocation = () => {
        if (!currentAllocation || currentAllocation.length === 0 || !quotes || quotes.length === 0) {
            return [];
        }

        return currentAllocation.map(asset => {
            const dollarAmount = (investmentAmount * asset.percent) / 100;
            let shares = null;
            let price = null;
            let tickerSymbol = null;

            // Determine which quote to use based on ticker
            if (asset.ticker === 'SPY or VOO') {
                // Try SPY first, then VOO, then QQQ as fallback
                const spyQuote = quotes.find(q => q.ticker === 'SPY');
                const vooQuote = quotes.find(q => q.ticker === 'VOO');
                const qqqQuote = quotes.find(q => q.ticker === 'QQQ');
                const quote = spyQuote || vooQuote || qqqQuote;
                if (quote) {
                    price = parseFloat(quote.price);
                    tickerSymbol = quote.ticker;
                    shares = price > 0 ? Math.floor(dollarAmount / price) : 0;
                }
            } else if (asset.ticker === 'QQQ') {
                // Use QQQ quote specifically
                const quote = quotes.find(q => q.ticker === 'QQQ');
                if (quote) {
                    price = parseFloat(quote.price);
                    tickerSymbol = quote.ticker;
                    shares = price > 0 ? Math.floor(dollarAmount / price) : 0;
                }
            } else if (asset.ticker === 'UPRO' || asset.ticker === 'TQQQ') {
                // Use the specific leveraged ETF quote
                const quote = quotes.find(q => q.ticker === asset.ticker);
                if (quote) {
                    price = parseFloat(quote.price);
                    tickerSymbol = quote.ticker;
                    shares = price > 0 ? Math.floor(dollarAmount / price) : 0;
                }
            }
            // T-bill or cash doesn't have shares

            return {
                ...asset,
                dollarAmount,
                shares,
                price,
                tickerSymbol,
            };
        });
    };

    const allocations = calculateAllocation();

    // Handle input change - validate multiples of 1000, minimum 1000
    const handleAmountChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value === '') {
            setInvestmentAmount(0);
            return;
        }
        const numValue = parseInt(value, 10);
        if (numValue >= 1000) {
            // Round down to nearest 1000
            const rounded = Math.floor(numValue / 1000) * 1000;
            setInvestmentAmount(rounded);
        } else if (numValue >= 0) {
            setInvestmentAmount(numValue);
        }
    };

    const handleStepUp = () => {
        setInvestmentAmount(prev => prev + 1000);
    };

    const handleStepDown = () => {
        setInvestmentAmount(prev => Math.max(1000, prev - 1000));
    };

    // Format input value for display
    const formatInputValue = () => {
        if (investmentAmount === 0) return '';
        return investmentAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    return (
        <div className="dashboard-module">
            <div className="mb-6">
                <h3 className="text-xl font-semibold mb-2 text-gray-300">
                    {title}
                </h3>
                <p className="text-sm text-gray-400 mb-4">
                    {description}
                </p>

                {/* Input Section */}
                <div className="flex items-center justify-center gap-4 mb-8">
                    <label htmlFor="investment-amount" className="text-sm font-medium text-gray-300">
                        {theoreticalInvestment}
                    </label>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleStepDown}
                            disabled={investmentAmount <= 1000}
                            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-300 rounded-lg transition-colors"
                            aria-label={decreaseAria}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                        </button>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                            <input
                                id="investment-amount"
                                type="text"
                                value={formatInputValue()}
                                onChange={handleAmountChange}
                                onBlur={(e) => {
                                    if (investmentAmount < 1000) {
                                        setInvestmentAmount(1000);
                                    } else {
                                        const rounded = Math.floor(investmentAmount / 1000) * 1000;
                                        setInvestmentAmount(rounded);
                                    }
                                }}
                                placeholder="10,000"
                                className="pl-8 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-32 text-center"
                            />
                        </div>
                        <button
                            onClick={handleStepUp}
                            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
                            aria-label={increaseAria}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </button>
                    </div>
                    <span className="text-xs text-gray-500">{multiplesHint}</span>
                </div>

                {/* Results Grid */}
                {allocations.length > 0 && investmentAmount >= 1000 ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {allocations.map((asset, index) => {
                            const borderColor = getColor(asset.colorVar);
                            const isCash = asset.ticker === 'T-bill or cash';

                            return (
                                <div
                                    key={index}
                                    className="bg-gray-800/50 border-2 rounded-lg p-5 hover:bg-gray-800 transition-colors"
                                    style={{ borderColor: borderColor + '60' }}
                                >
                                    {/* Asset Header */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <div
                                            className="w-3 h-3 rounded-full"
                                            style={{ backgroundColor: borderColor }}
                                        ></div>
                                        <div className="flex-1">
                                            <h4 className="text-gray-200 font-semibold text-sm">{asset.name}</h4>
                                            <p className="text-xs text-gray-400">{asset.ticker}</p>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-lg font-bold" style={{ color: borderColor }}>
                                                {asset.percent}%
                                            </span>
                                        </div>
                                    </div>

                                    {/* Dollar Amount */}
                                    <div className="mb-3">
                                        <p className="text-xs text-gray-500 mb-1">{allocationLabel}</p>
                                        <p className="text-2xl font-bold text-gray-100">
                                            {formatCurrency(asset.dollarAmount)}
                                        </p>
                                    </div>

                                    {/* Shares or Cash */}
                                    {!isCash && asset.shares !== null && asset.price !== null ? (
                                        <>
                                            <div className="mb-2">
                                                <p className="text-xs text-gray-500 mb-1">{sharesToPurchase}</p>
                                                <p className="text-xl font-semibold text-gray-200">
                                                    {asset.shares} {asset.shares === 1 ? share : shares}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 mb-1">{currentPrice}</p>
                                                <p className="text-sm font-medium text-gray-300">
                                                    {formatCurrency(asset.price)} <span className="text-xs text-gray-500">({asset.tickerSymbol})</span>
                                                </p>
                                            </div>
                                            {asset.shares > 0 && (
                                                <p className="text-xs text-gray-500 mt-2">
                                                    ≈ {formatCurrency(asset.shares * asset.price)} {allocatedLabel}
                                                </p>
                                            )}
                                        </>
                                    ) : isCash ? (
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">{cashAllocation}</p>
                                            <p className="text-sm font-medium text-gray-300">
                                                {formatCurrency(asset.dollarAmount)} {cashAmountText}
                                            </p>
                                        </div>
                                    ) : asset.percent === 0 ? (
                                        <div>
                                            <p className="text-xs text-gray-500">{notAllocated}</p>
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="text-xs text-yellow-500">{priceUnavailable}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : investmentAmount < 1000 ? (
                    <div className="text-center py-8 text-gray-400">
                        <p>{minAmountMessage}</p>
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-400">
                        <p>{loadingMessage}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- NEW: LIVE QUOTE WIDGET ---

/**
 * A component to display live market quotes from API.
 */
const LiveQuoteWidget = ({ quotes }) => {
    const { t } = useTranslation();
    const loadingQuotesText = t('dashboard.loading.quotes', 'Loading quotes...');

    if (!quotes || quotes.length === 0) {
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-gray-400 text-sm">{loadingQuotesText}</div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quotes.map((quote) => {
                const isPositive = parseFloat(quote.change) >= 0;
                return (
                    <div key={quote.ticker} className="quote-item">
                        <div>
                            <span className="quote-ticker">{quote.ticker}</span>
                            <span className="quote-price">${quote.price}</span>
                        </div>
                        <span
                            className={`quote-change ${isPositive ? 'positive' : 'negative'}`}
                        >
                            {isPositive ? '+' : ''}{quote.change}%
                        </span>
                    </div>
                );
            })}
        </div>
    );
};


// --- 3. REACT COMPONENT (App) ---
// Custom component for the Alerts (REMOVED)
/*
const AlertPanel = ({ status }) => (
    // ... (Content removed as it's no longer used)
);
*/


export default function App() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dashboardText = t('dashboard', { returnObjects: true, defaultValue: {} }) || {};
    const headerText = dashboardText.header || {};
    const accountText = t('account', { returnObjects: true, defaultValue: {} }) || {};
    const dashboardsText = accountText.dashboards || {};
    // Override "Global Tech Dashboard" to "Hybrid Global Dashboard" for this dashboard
    // Use proper translation from account.dashboards.hybrid for Spanish
    const isSpanish = (i18n.language || 'en').startsWith('es');
    const taglineText = isSpanish
        ? (headerText.tagline === 'Panel Global de Tecnología' ? (dashboardsText.hybrid || 'Panel Híbrido') : (headerText.tagline || dashboardsText.hybrid || 'Panel Híbrido'))
        : (headerText.tagline === 'Global Tech Dashboard' ? 'Hybrid Global Dashboard' : (headerText.tagline || 'Global Stocks + Technology Leverage Dashboard'));
    const collapsedTaglineText = isSpanish
        ? (headerText.collapsedTagline === 'Panel Global de Tecnología' ? (dashboardsText.hybrid || 'Panel Híbrido') : (headerText.collapsedTagline || taglineText))
        : (headerText.collapsedTagline === 'Global Tech Dashboard' ? 'Hybrid Global Dashboard' : (headerText.collapsedTagline || taglineText));
    const riskText = dashboardText.riskProfiles || {};
    const riskLabels = riskText.labels || {};
    const riskDescriptions = riskText.descriptions || {};
    const loadingText = dashboardText.loading || {};
    const portfolioText = dashboardText.portfolioModule || {};
    const marketSnapshotText = dashboardText.marketSnapshot || {};
    const delayDisclaimerText = marketSnapshotText.delayDisclaimer ||
        ((i18n.language || 'en').startsWith('es')
            ? 'Los datos de mercado tienen un retraso aproximado de 15 minutos.'
            : 'Market data delayed by ~15 minutes.');
    const allocationText = dashboardText.allocationModule || {};
    const exposureText = dashboardText.exposureModule || {};
    const scatterText = dashboardText.scatterModule || {};
    const relativePerformanceText = dashboardText.relativePerformance || {};
    const volatilityText = dashboardText.volatilityModule || {};
    const summaryText = dashboardText.summaryModule || {};
    const legalDisclaimerText = dashboardText.legalDisclaimer || {};
    const assistantButtonText = dashboardText.assistantButton || {};
    const tutorialText = dashboardText.tutorial || {};
    const investmentCalculatorText = dashboardText.investmentCalculator || {};
    const languageOptions = [
        { code: 'en', label: 'EN' },
        { code: 'es', label: 'ES' }
    ];
    const activeLanguage = (i18n.language || 'en').startsWith('es') ? 'es' : 'en';
    const languageNames = { en: 'English', es: 'Español' };

    const handleLanguageChange = (code) => {
        if (code !== activeLanguage) {
            i18n.changeLanguage(code);
        }
    };

    const renderRichText = (text) => ({
        __html: (text || '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    });

    const defaultLegalParagraphs = [
        'Altradenative is an analytical, data-driven information service designed to support and optimize stock investment decision-making. The insights and metrics provided through our systematic and periodic dashboards are intended solely for informational and analytical purposes and do not constitute trading advice of any sort.',
        'Altradenative is not a broker, does not execute trades, and does not maintain any commercial relationship with subscribers beyond the provision of investment-related information. Users are solely responsible for interpreting and utilizing the data according to their individual needs and circumstances.',
        'Investing in stocks and ETFs carries the risk of capital loss. Altradenative assumes no responsibility for any investment decisions made by users, nor for any loss of funds that may result from such decisions.',
        'For neutral and aggressive (seeker) profiles, Altradenative\'s algorithm may also generate losses greater than those of global indices.'
    ];
    const legalParagraphs = legalDisclaimerText.paragraphs || defaultLegalParagraphs;

    const [risk, setRisk] = useState('Averse');
    const [isCssReady, setIsCssReady] = useState(false); 
    const [isRiskExpanded, setIsRiskExpanded] = useState(true);

    // --- NEW: State for Tutorial Modal ---
    const [showTutorial, setShowTutorial] = useState(false);
    // This state controls the "Show on startup" toggle
    const [showTutorialOnStartup, setShowTutorialOnStartup] = useState(true);
    
    // --- NEW: State for Virtual Assistant ---
    const [showAssistant, setShowAssistant] = useState(false);
    
    // --- NEW: State for Market Status ---
    const [marketStatus, setMarketStatus] = useState({ isOpen: false, statusLabel: 'Checking...' });

    // --- API Data States ---
    const [riskProfilesData, setRiskProfilesData] = useState(null);
    const [currentAllocation, setCurrentAllocation] = useState([]);
    const [performanceData, setPerformanceData] = useState({ time_points: [], native_returns: [], spy_returns: [] });
    const [quotes, setQuotes] = useState([]);
    const [exposureTimelineData, setExposureTimelineData] = useState({ timeline: [], current: null });
    const [correlationData, setCorrelationData] = useState({ scatterData: [], lineData: [], m: "0" });
    const [relativePerformanceData, setRelativePerformanceData] = useState({});
    const [volatilityData, setVolatilityData] = useState({});
    const [marketContext, setMarketContext] = useState(null);
    
    // --- Loading States ---
    const [isLoading, setIsLoading] = useState({
        riskProfiles: true,
        marketStatus: true,
        allocation: true,
        performance: true,
        quotes: true,
        exposureTimeline: true,
        correlation: true,
        relativePerformance: true,
        volatility: true,
        marketContext: true
    });
    
    // --- Error States ---
    const [errors, setErrors] = useState({});
    
    const performanceRef = useRef(null); 
    const exposureRef = useRef(null); 
    const exposureTimelineRef = useRef(null);
    const exposureTimelineZoomControlRef = useRef(null);
    const scatterRef = useRef(null); 
    
    // Mutable context for ECharts 
    const chartContextRef = useRef({
        performanceChart: null, 
        exposureChart: null,
        scatterChart: null,
        // Add keys for new charts
        chart3m: null,
        chart1y: null,
        chart3y: null,
        // Add keys for volatility charts
        chartVol3m: null,
        chartVol1y: null,
        chartVol3y: null
    });

    // Helper to safely get CSS variables
    const getCssVariable = useCallback((name) => {
        if (typeof window !== 'undefined') {
            const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
            return value || '#3b3b3b'; 
        }
        return '#3b3b3b';
    }, []);
    


    const updateRiskProfile = useCallback((newRisk) => {
        if (risk === newRisk) return;
        setRisk(newRisk);
        // Data will be refetched via useEffect when risk changes
    }, [risk]);
    
    const toggleRiskExpanded = useCallback(() => {
        setIsRiskExpanded(prev => !prev);
    }, []);

    // Memoize the exposure initialization
    const initializeExposureChartMemoized = useCallback((currentRisk, allocation) => {
        initializeExposureChart(exposureRef, getCssVariable, chartContextRef.current, currentRisk, allocation);
    }, [getCssVariable]);

    // Memoize the scatter chart initialization
    const initializeScatterChartMemoized = useCallback((currentRisk) => {
        initializeScatterChart(scatterRef, getCssVariable, currentRisk, chartContextRef.current, correlationData);
    }, [getCssVariable, correlationData]);


    // --- 1. PERFORMANCE & EXPOSURE CHART INITIALIZATION (Runs on mount and risk change) ---
    useEffect(() => {
        const chartDom = performanceRef.current;
        const context = chartContextRef.current;

        if (!chartDom || !isCssReady) return;

        // Function to handle resize for ECharts 
        const handleResize = () => {
            if (context.performanceChart) context.performanceChart.resize();
            if (context.exposureChart) context.exposureChart.resize(); 
            if (context.scatterChart) context.scatterChart.resize(); 
            // Add resize for new charts
            if (context.chart3m) context.chart3m.resize();
            if (context.chart1y) context.chart1y.resize();
            if (context.chart3y) context.chart3y.resize();
            // Add resize for volatility charts
            if (context.chartVol3m) context.chartVol3m.resize();
            if (context.chartVol1y) context.chartVol1y.resize();
            if (context.chartVol3y) context.chartVol3y.resize();
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3417',message:'Before getInstanceByDom exposureTimeline',data:{hasExposureTimelineRef:!!exposureTimelineRef,hasCurrent:!!exposureTimelineRef?.current,currentType:exposureTimelineRef?.current?.constructor?.name,currentNodeName:exposureTimelineRef?.current?.nodeName},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
            // #endregion
            const exposureTimelineChartInstance = exposureTimelineRef?.current ? echarts.getInstanceByDom(exposureTimelineRef.current) : null;
            if (exposureTimelineChartInstance) exposureTimelineChartInstance.resize();
        };

        const timer = setTimeout(() => {
            if (typeof echarts.init !== 'function') {
                console.error("ECharts library is not available.");
                return;
            }
            
            // 1. Initialize Performance Chart
            if (context.performanceChart) context.performanceChart.dispose();
            context.performanceChart = echarts.init(chartDom); 
            
            const nativePrimaryColor = getCssVariable('--text-primary');
            const spyColor = getCssVariable('--text-light'); 
            const chartAxisColor = getCssVariable('--chart-panel-bg');
            const footerColor = getCssVariable('--footer-color'); 
            
            // Use API performance data
            const nativeData = performanceData.native_returns || [];
            const spyData = performanceData.spy_returns || [];
            const timePoints = performanceData.time_points || [];

            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3427',message:'Performance chart initialization with data',data:{currentRisk:risk,performanceDataState:performanceData,nativeDataLength:nativeData.length,spyDataLength:spyData.length,timePointsLength:timePoints.length,nativeDataSample:nativeData.slice(0,3),spyDataSample:spyData.slice(0,3),timePointsSample:timePoints.slice(0,3)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
            // #endregion

            if (nativeData.length === 0 || spyData.length === 0 || timePoints.length === 0) {
                return; // Don't render chart if data is not ready
            }

            const allValues = [...nativeData, ...spyData]
                .map(value => Number(value))
                .filter(value => !Number.isNaN(value));

            const observedMin = allValues.length ? Math.min(...allValues) : 0;
            const observedMax = allValues.length ? Math.max(...allValues) : 0;
            const range = observedMax - observedMin;

            const padding = range * 0.1;
            const paddedMin = Math.floor((observedMin - padding) * 10) / 10;
            const paddedMax = Math.ceil((observedMax + padding) * 10) / 10;
            const yAxisInterval = Math.max(5, Math.ceil((paddedMax - paddedMin) / 6));

            const option = {
                backgroundColor: 'transparent',
                grid: { top: '10%', left: '3%', right: '3%', bottom: '10%', containLabel: true },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'line', lineStyle: { color: '#ccc', width: 1, type: 'solid', opacity: 0.5 } },
                    backgroundColor: 'rgba(46, 50, 56, 0.75)',
                    textStyle: { color: '#fff', fontSize: 12 },
                    borderColor: 'transparent',
                    padding: 10,
                    formatter: function(params) {
                        let html = `<div style="font-weight: bold; color: ${footerColor}; text-align: center; font-size: 1em; margin-bottom: 5px;">${params[0].name}</div>`;
                        params.forEach(item => {
                            const color = item.seriesName === 'ALTRADENATIVE' ? nativePrimaryColor : spyColor;
                            html += `<div style="display:flex; justify-content:space-between; width:100%; font-size: 0.9em; padding: 2px 0;">
                                            <span>${item.seriesName}:</span> 
                                            <span style="font-weight:bold; color: ${color};">${item.value}%</span>
                                        </div>`;
                        });
                        return html;
                    }
                },
                xAxis: {
                    type: 'category', data: timePoints,
                    axisLine: { lineStyle: { color: '#4a4a4a' } },
                    axisLabel: { color: '#ccc', fontSize: 10 },
                    axisTick: { show: false }
                },
                yAxis: {
                    type: 'value',
                    min: paddedMin,
                    max: paddedMax,
                    interval: yAxisInterval,
                    axisLine: { show: false },
                    splitLine: { lineStyle: { color: '#3b3b3b' } },
                    axisLabel: { formatter: '{value}%', color: '#ccc', fontSize: 10 }
                },
                series: [
                    {
                        name: 'ALTRADENATIVE', type: 'line', smooth: true, data: nativeData,
                        itemStyle: { color: nativePrimaryColor, opacity: 0, symbolSize: 0, borderColor: chartAxisColor, borderWidth: 2 },
                        emphasis: { itemStyle: { opacity: 1, symbolSize: 10, color: nativePrimaryColor } },
                        lineStyle: { width: 2, color: nativePrimaryColor },
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: 'rgba(133, 200, 255, 0.3)' }, 
                                { offset: 1, color: 'rgba(133, 200, 255, 0)' }  
                            ])
                        }, z: 2
                    },
                    {
                        name: 'SPY', type: 'line', smooth: true, data: spyData,
                        itemStyle: { color: spyColor, opacity: 0, symbolSize: 0, borderColor: chartAxisColor, borderWidth: 1 },
                        emphasis: { itemStyle: { opacity: 1, symbolSize: 8, color: spyColor } },
                        lineStyle: { width: 1, color: spyColor, opacity: 0.8 },
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: 'rgba(255, 255, 255, 0.15)' }, 
                                { offset: 1, color: 'rgba(255, 255, 255, 0)' }  
                            ])
                        }, z: 1
                    }
                ]
            };

            context.performanceChart.setOption(option);
            
            // 2. Initialize Exposure Chart
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3530',message:'Before initializeExposureChartMemoized',data:{risk:risk,currentAllocation:currentAllocation,allocationType:currentAllocation?.constructor?.name,allocationLength:currentAllocation?.length,allocationIsArray:Array.isArray(currentAllocation),allocationSample:currentAllocation?.slice?.(0,2)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
            // #endregion
            if (currentAllocation && Array.isArray(currentAllocation) && currentAllocation.length > 0) {
                initializeExposureChartMemoized(risk, currentAllocation); // Pass dynamic allocation
            } else {
                // #region agent log
                fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3532',message:'Skipping exposure chart - invalid allocation',data:{risk:risk,currentAllocation:currentAllocation,allocationType:currentAllocation?.constructor?.name,allocationLength:currentAllocation?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
                // #endregion
            }
            
            window.addEventListener('resize', handleResize);
        }, 50);

        // Cleanup for Performance and Exposure
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', handleResize);
            if (context.performanceChart) { context.performanceChart.dispose(); }
            if (context.exposureChart) { context.exposureChart.dispose(); }
            // Note: Module 4 charts (3m, 1y, 3y) cleanup is handled in their own component
        };
    }, [getCssVariable, initializeExposureChartMemoized, risk, isCssReady, currentAllocation, performanceData]); 
    
    
    // --- 2. SCATTER CHART INITIALIZATION (Isolated useEffect for better control) ---
    useEffect(() => {
        const context = chartContextRef.current;
        if (!isCssReady) return;

        // Scatter chart re-initializes on risk change or correlation data update
        if (correlationData.scatterData.length > 0) {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3534',message:'Scatter chart initialization triggered',data:{currentRisk:risk,correlationDataState:correlationData,scatterDataLength:correlationData.scatterData.length,scatterDataSample:correlationData.scatterData.slice(0,3),m:correlationData.m},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
            // #endregion
            initializeScatterChartMemoized(risk);
        }
        
        // Cleanup for Scatter Chart
        return () => {
            if (context.scatterChart) { context.scatterChart.dispose(); }
        };
    }, [isCssReady, initializeScatterChartMemoized, risk, correlationData]);


    // --- API Data Fetching Functions ---
    const fetchRiskProfiles = useCallback(async () => {
        setIsLoading(prev => ({ ...prev, riskProfiles: true }));
        const { data, error } = await api.getRiskProfiles(DASHBOARD_VARIANT);
        if (error) {
            setErrors(prev => ({ ...prev, riskProfiles: error }));
        } else {
            setRiskProfilesData(data);
        }
        setIsLoading(prev => ({ ...prev, riskProfiles: false }));
    }, []);

    const fetchMarketStatus = useCallback(async () => {
        setIsLoading(prev => ({ ...prev, marketStatus: true }));
        const { data, error } = await api.getMarketStatus(DASHBOARD_VARIANT);
        if (error) {
            setErrors(prev => ({ ...prev, marketStatus: error }));
        } else {
            setMarketStatus(data || { isOpen: false, statusLabel: 'Status Unknown' });
        }
        setIsLoading(prev => ({ ...prev, marketStatus: false }));
    }, []);

    const fetchAllocation = useCallback(async (riskProfile) => {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3616',message:'fetchAllocation called',data:{riskProfile:riskProfile,variant:DASHBOARD_VARIANT},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        setIsLoading(prev => ({ ...prev, allocation: true }));
        
        // Check local cache first
        const cachedData = dashboardCache.get(DASHBOARD_VARIANT, riskProfile, 'allocation');
        if (cachedData) {
            const mappedAllocation = mapAllocationFromAPI(cachedData);
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3622',message:'Setting allocation from cache',data:{riskProfile:riskProfile,mappedAllocationLength:mappedAllocation?.length,mappedAllocationSample:mappedAllocation?.[0]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
            // #endregion
            setCurrentAllocation(mappedAllocation);
            setIsLoading(prev => ({ ...prev, allocation: false }));
            return;
        }
        
        // Cache miss - fetch from API
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3629',message:'Fetching allocation from API',data:{riskProfile:riskProfile,variant:DASHBOARD_VARIANT},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
        // #endregion
        const { data, error } = await api.getAllocation(riskProfile, DASHBOARD_VARIANT);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3630',message:'API response received',data:{riskProfile:riskProfile,hasData:!!data,hasError:!!error,error:error,dataType:data?.constructor?.name,dataIsArray:Array.isArray(data),dataLength:data?.length,dataSample:data?.[0]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
        // #endregion
        if (error) {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3631',message:'API error - setting error state',data:{riskProfile:riskProfile,error:error,currentAllocationState:'NOT SET - will remain empty'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
            // #endregion
            setErrors(prev => ({ ...prev, allocation: error }));
        } else if (data) {
            const mappedAllocation = mapAllocationFromAPI(data);
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3633',message:'Setting allocation from API response',data:{riskProfile:riskProfile,mappedAllocationLength:mappedAllocation?.length,mappedAllocationSample:mappedAllocation?.[0]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
            // #endregion
            setCurrentAllocation(mappedAllocation);
            // Store in cache
            dashboardCache.set(DASHBOARD_VARIANT, riskProfile, 'allocation', data);
        } else {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3637',message:'API returned no data and no error',data:{riskProfile:riskProfile,data:data,error:error,currentAllocationState:'NOT SET - will remain empty'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
            // #endregion
        }
        setIsLoading(prev => ({ ...prev, allocation: false }));
    }, []);

    const fetchPerformance = useCallback(async (riskProfile) => {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3598',message:'fetchPerformance called',data:{riskProfile:riskProfile,currentRiskState:risk},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        setIsLoading(prev => ({ ...prev, performance: true }));
        
        // Check local cache first
        const cachedData = dashboardCache.get(DASHBOARD_VARIANT, riskProfile, 'performance');
        if (cachedData) {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3602',message:'Using cached performance data',data:{riskProfile:riskProfile,cachedData:cachedData},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
            // #endregion
            setPerformanceData(cachedData);
            setIsLoading(prev => ({ ...prev, performance: false }));
            return;
        }
        
        // Cache miss - fetch from API
        const { data, error } = await api.getPerformance(riskProfile, DASHBOARD_VARIANT);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3610',message:'API response received for performance',data:{riskProfile:riskProfile,hasData:!!data,hasError:!!error,dataSample:data?{time_points:data.time_points,native_returns_length:data.native_returns?.length,spy_returns_length:data.spy_returns?.length}:null},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
        // #endregion
        if (error) {
            setErrors(prev => ({ ...prev, performance: error }));
        } else if (data) {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3614',message:'Setting performance data in state',data:{riskProfile:riskProfile,data:data},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
            // #endregion
            setPerformanceData(data);
            // Store in cache
            dashboardCache.set(DASHBOARD_VARIANT, riskProfile, 'performance', data);
        }
        setIsLoading(prev => ({ ...prev, performance: false }));
    }, []);

    const fetchQuotes = useCallback(async () => {
        setIsLoading(prev => ({ ...prev, quotes: true }));
        const { data, error } = await api.getQuotes(DASHBOARD_VARIANT);
        if (error) {
            setErrors(prev => ({ ...prev, quotes: error }));
        } else if (data) {
            setQuotes(data);
        }
        setIsLoading(prev => ({ ...prev, quotes: false }));
    }, []);

    const fetchExposureTimeline = useCallback(async (riskProfile) => {
        setIsLoading(prev => ({ ...prev, exposureTimeline: true }));
        
        // Check local cache first
        const cachedData = dashboardCache.get(DASHBOARD_VARIANT, riskProfile, 'exposure-timeline');
        if (cachedData) {
            setExposureTimelineData(cachedData || { timeline: [], current: null });
            setIsLoading(prev => ({ ...prev, exposureTimeline: false }));
            return;
        }
        
        logger.log(`[Fetch] Requesting exposure timeline for risk profile: ${riskProfile} with frequency=daily`);
        
        const { data, error } = await api.getExposureTimeline(riskProfile, 'daily', DASHBOARD_VARIANT);
        
        if (error) {
            logger.error(`[Fetch] Error fetching exposure timeline:`, error);
            setErrors(prev => ({ ...prev, exposureTimeline: error }));
        } else if (data) {
            // Comprehensive data validation
            const timeline = data.timeline || [];
            logger.log(`[Fetch] Processing ${timeline.length} timeline data points`);
            
            if (timeline.length > 0) {
                // Validate date spacing across multiple points
                const dateSpacings = [];
                for (let i = 1; i < Math.min(10, timeline.length); i++) {
                    const date1 = new Date(timeline[i - 1].date);
                    const date2 = new Date(timeline[i].date);
                    const daysDiff = Math.round((date2 - date1) / (1000 * 60 * 60 * 24));
                    dateSpacings.push(daysDiff);
                }
                
                const avgSpacing = dateSpacings.reduce((a, b) => a + b, 0) / dateSpacings.length;
                const maxSpacing = Math.max(...dateSpacings);
                const minSpacing = Math.min(...dateSpacings);
                
                logger.log(`[Fetch] Date spacing analysis:`, {
                    average: avgSpacing.toFixed(1),
                    min: minSpacing,
                    max: maxSpacing,
                    first10Intervals: dateSpacings
                });
                
                if (maxSpacing > 1) {
                    logger.error(`[Fetch] ERROR: Data contains ${maxSpacing}-day intervals! Expected daily (1-day) intervals.`);
                    logger.error(`[Fetch] This indicates the backend API may not be returning daily data despite frequency=daily parameter.`);
                } else {
                    logger.log(`[Fetch] ✓ Confirmed daily data: all intervals are 1 day`);
                }
                
                logger.log(`[Fetch] Date range: ${timeline[0].date} to ${timeline[timeline.length - 1].date}`);
            } else {
                logger.warn(`[Fetch] WARNING: Received empty timeline array`);
            }
            
            // Store complete data without any filtering
            logger.log(`[Fetch] Storing ${timeline.length} data points in state`);
            // #region agent log
            const lastTimelineItem = timeline.length > 0 ? timeline[timeline.length - 1] : null;
            const currentDataItem = data?.current || null;
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3583',message:'Frontend: Received exposure timeline data from API','data':{timelineLength:timeline.length,lastTimelineItem:lastTimelineItem,currentDataItem:currentDataItem,datesMatch:lastTimelineItem?.date===currentDataItem?.date,valuesMatch:JSON.stringify(lastTimelineItem)===JSON.stringify(currentDataItem)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
            // #endregion
            setExposureTimelineData(data || { timeline: [], current: null });
            // Store in cache
            dashboardCache.set(DASHBOARD_VARIANT, riskProfile, 'exposure-timeline', data);
        } else {
            logger.warn(`[Fetch] No data received from API`);
        }
        
        setIsLoading(prev => ({ ...prev, exposureTimeline: false }));
    }, []);

    const fetchCorrelation = useCallback(async (riskProfile) => {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3705',message:'fetchCorrelation called',data:{riskProfile:riskProfile,currentRiskState:risk},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        setIsLoading(prev => ({ ...prev, correlation: true }));
        
        // Check local cache first
        const cachedData = dashboardCache.get(DASHBOARD_VARIANT, riskProfile, 'correlation');
        if (cachedData) {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3709',message:'Using cached correlation data',data:{riskProfile:riskProfile,cachedData:cachedData},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
            // #endregion
            setCorrelationData(cachedData);
            setIsLoading(prev => ({ ...prev, correlation: false }));
            return;
        }
        
        // Cache miss - fetch from API
        const { data, error } = await api.getCorrelation(riskProfile, DASHBOARD_VARIANT);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3717',message:'API response received for correlation',data:{riskProfile:riskProfile,hasData:!!data,hasError:!!error,dataSample:data?{scatterDataLength:data.scatterData?.length,m:data.m}:null},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
        // #endregion
        if (error) {
            setErrors(prev => ({ ...prev, correlation: error }));
        } else if (data) {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:3721',message:'Setting correlation data in state',data:{riskProfile:riskProfile,data:data},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
            // #endregion
            setCorrelationData(data);
            // Store in cache
            dashboardCache.set(DASHBOARD_VARIANT, riskProfile, 'correlation', data);
        }
        setIsLoading(prev => ({ ...prev, correlation: false }));
    }, []);

    const fetchRelativePerformance = useCallback(async () => {
        setIsLoading(prev => ({ ...prev, relativePerformance: true }));
        
        // Check local cache first
        const cachedData = dashboardCache.get(DASHBOARD_VARIANT, 'all', 'relative-performance');
        if (cachedData) {
            setRelativePerformanceData(cachedData);
            setIsLoading(prev => ({ ...prev, relativePerformance: false }));
            return;
        }
        
        // Cache miss - fetch from API
        const { data, error } = await api.getRelativePerformance(DASHBOARD_VARIANT);
        if (error) {
            setErrors(prev => ({ ...prev, relativePerformance: error }));
        } else if (data) {
            setRelativePerformanceData(data);
            // Store in cache
            dashboardCache.set(DASHBOARD_VARIANT, 'all', 'relative-performance', data);
        }
        setIsLoading(prev => ({ ...prev, relativePerformance: false }));
    }, []);

    const fetchVolatility = useCallback(async () => {
        setIsLoading(prev => ({ ...prev, volatility: true }));
        
        // Check local cache first
        const cachedData = dashboardCache.get(DASHBOARD_VARIANT, 'all', 'volatility');
        if (cachedData) {
            setVolatilityData(cachedData);
            setIsLoading(prev => ({ ...prev, volatility: false }));
            return;
        }
        
        // Cache miss - fetch from API
        const { data, error } = await api.getVolatility(DASHBOARD_VARIANT);
        if (error) {
            setErrors(prev => ({ ...prev, volatility: error }));
        } else if (data) {
            setVolatilityData(data);
            // Store in cache
            dashboardCache.set(DASHBOARD_VARIANT, 'all', 'volatility', data);
        }
        setIsLoading(prev => ({ ...prev, volatility: false }));
    }, []);

    const fetchMarketContext = useCallback(async (riskProfile) => {
        setIsLoading(prev => ({ ...prev, marketContext: true }));
        
        // Check local cache first
        const cachedData = dashboardCache.get(DASHBOARD_VARIANT, riskProfile, 'market-context');
        if (cachedData) {
            setMarketContext(cachedData);
            setIsLoading(prev => ({ ...prev, marketContext: false }));
            return;
        }
        
        // Cache miss - fetch from API
        const { data, error } = await api.getMarketContext(riskProfile, DASHBOARD_VARIANT);
        if (error) {
            setErrors(prev => ({ ...prev, marketContext: error }));
        } else if (data) {
            setMarketContext(data);
            // Store in cache
            dashboardCache.set(DASHBOARD_VARIANT, riskProfile, 'market-context', data);
        }
        setIsLoading(prev => ({ ...prev, marketContext: false }));
    }, []);

    // --- Initial Data Load (runs once on mount) ---
    useEffect(() => {
        const injectedStyle = injectStyles();
        
        // Fetch initial data in parallel using Promise.all for better performance
        // Try batch endpoint first, fallback to individual calls if needed
        const loadInitialData = async () => {
            try {
                // Try batch endpoint for risk-dependent data
                const { data: batchData, error: batchError } = await api.getBatchData(
                    ['allocation', 'performance', 'correlation', 'exposure-timeline', 
                     'market-context', 'quotes', 'relative-performance', 'volatility'],
                    risk,
                    DASHBOARD_VARIANT,
                    'daily'
                );
                
                // Always fetch risk profiles and market status (not in batch endpoint)
                await Promise.all([
                    fetchRiskProfiles(),
                    fetchMarketStatus()
                ]);
                
                if (!batchError && batchData) {
                    // Helper to check if response has error
                    const hasError = (data) => !data || (typeof data === 'object' && 'error' in data);
                    
                    // Process batch response
                    if (batchData.allocation && !hasError(batchData.allocation)) {
                        const mappedAllocation = mapAllocationFromAPI(batchData.allocation);
                        setCurrentAllocation(mappedAllocation);
                        dashboardCache.set(DASHBOARD_VARIANT, risk, 'allocation', batchData.allocation);
                    }
                    if (batchData.performance && !hasError(batchData.performance)) {
                        setPerformanceData(batchData.performance);
                        dashboardCache.set(DASHBOARD_VARIANT, risk, 'performance', batchData.performance);
                    }
                    if (batchData.correlation && !hasError(batchData.correlation)) {
                        setCorrelationData(batchData.correlation);
                        dashboardCache.set(DASHBOARD_VARIANT, risk, 'correlation', batchData.correlation);
                    }
                    if (batchData['exposure-timeline'] && !hasError(batchData['exposure-timeline'])) {
                        setExposureTimelineData(batchData['exposure-timeline'] || { timeline: [], current: null });
                        dashboardCache.set(DASHBOARD_VARIANT, risk, 'exposure-timeline', batchData['exposure-timeline']);
                    }
                    if (batchData['market-context'] && !hasError(batchData['market-context'])) {
                        setMarketContext(batchData['market-context']);
                        dashboardCache.set(DASHBOARD_VARIANT, risk, 'market-context', batchData['market-context']);
                    } else if (batchData['market-context'] && hasError(batchData['market-context'])) {
                        console.error('Market context error:', batchData['market-context'].error);
                    }
                    if (batchData.quotes && !hasError(batchData.quotes)) {
                        setQuotes(batchData.quotes);
                    }
                    if (batchData['relative-performance'] && !hasError(batchData['relative-performance'])) {
                        setRelativePerformanceData(batchData['relative-performance']);
                    }
                    if (batchData.volatility && !hasError(batchData.volatility)) {
                        setVolatilityData(batchData.volatility);
                    }
                    
                    // Clear all loading states after batch processing
                    setIsLoading(prev => ({
                        ...prev,
                        allocation: false,
                        performance: false,
                        correlation: false,
                        exposureTimeline: false,
                        marketContext: false,
                        quotes: false,
                        relativePerformance: false,
                        volatility: false
                    }));
                } else {
                    // Fallback to parallel individual calls if batch fails
                    await Promise.all([
                        fetchAllocation(risk),
                        fetchPerformance(risk),
                        fetchQuotes(),
                        fetchExposureTimeline(risk),
                        fetchCorrelation(risk),
                        fetchRelativePerformance(),
                        fetchVolatility(),
                        fetchMarketContext(risk)
                    ]);
                }
            } catch (error) {
                console.error('Error loading initial data:', error);
                // Fallback to sequential individual calls on error
                fetchRiskProfiles();
                fetchMarketStatus();
                fetchAllocation(risk);
                fetchPerformance(risk);
                fetchQuotes();
                fetchExposureTimeline(risk);
                fetchCorrelation(risk);
                fetchRelativePerformance();
                fetchVolatility();
                fetchMarketContext(risk);
            }
        };
        
        loadInitialData();

        // Check localStorage for tutorial preference
        try {
            const storedPreference = localStorage.getItem('showAltradeNativeTutorial');
            if (storedPreference === 'false') {
                setShowTutorial(false);
                setShowTutorialOnStartup(false);
            } else {
                setShowTutorial(true);
                setShowTutorialOnStartup(true);
            }
        } catch (error) {
            console.error("Could not access localStorage. Defaulting to show tutorial.", error);
            setShowTutorial(true);
            setShowTutorialOnStartup(true);
        }

        const timer = setTimeout(() => {
            setIsCssReady(true);
        }, 100);

        return () => {
            clearTimeout(timer);
            // Clean up injected styles when component unmounts
            if (injectedStyle && injectedStyle.parentNode) {
                injectedStyle.remove();
            }
            // Also remove by ID as fallback
            const styleById = document.getElementById('dashboard-hybrid-styles');
            if (styleById) {
                styleById.remove();
            }
        };
    }, []); // Empty dependency array, runs only once on mount

    // --- Refresh data when risk profile changes ---
    useEffect(() => {
        if (risk) {
            // Use parallel Promise.all for better performance
            Promise.all([
                fetchAllocation(risk),
                fetchPerformance(risk),
                fetchCorrelation(risk),
                fetchExposureTimeline(risk),
                fetchMarketContext(risk)
            ]).catch(error => {
                console.error('Error refreshing data on risk change:', error);
            });
        }
    }, [risk, fetchAllocation, fetchPerformance, fetchCorrelation, fetchExposureTimeline, fetchMarketContext]);

    // --- 5-minute refresh mechanism ---
    useEffect(() => {
        const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds
        
        const refreshData = () => {
            // Refresh live data that changes frequently
            fetchMarketStatus();
            fetchQuotes();
            
            // Refresh risk-dependent data if risk is set
            if (risk) {
                fetchAllocation(risk);
                fetchExposureTimeline(risk);
                // Optionally refresh performance and correlation less frequently
            }
        };
        
        const intervalId = setInterval(refreshData, REFRESH_INTERVAL);
        
        return () => clearInterval(intervalId);
    }, [risk, fetchMarketStatus, fetchQuotes, fetchExposureTimeline, fetchAllocation]);

    // --- NEW: Handler functions for Tutorial Modal ---
    const handleCloseTutorial = () => {
        setShowTutorial(false);
    };

    const handleToggleStartup = (newValue) => {
        setShowTutorialOnStartup(newValue);
        try {
            localStorage.setItem('showAltradeNativeTutorial', newValue);
        } catch (error) {
            console.error("Could not save tutorial preference to localStorage.", error);
        }
    };


    // Helper to render Donut Chart inline style 
    const renderIndividualDonutStyle = (percent, colorVar) => {
        const fillEnd = percent; 

        return {
            background: `conic-gradient(
                var(${colorVar}) 0%, 
                var(${colorVar}) ${fillEnd}%, 
                var(--empty-donut-color) ${fillEnd}%, 
                var(--empty-donut-color) 100%
            )`,
            transform: 'rotate(270deg)',
            transformOrigin: 'center center',
        };
    };


    // Determine if any critical data is loading (show spinner during initial load or major refresh)
    const isGlobalLoading = isLoading.allocation || isLoading.performance || isLoading.correlation || 
                           isLoading.exposureTimeline || isLoading.relativePerformance || isLoading.volatility;

    return (
        <div className="bg-[#212529] min-h-screen flex flex-col">
            {/* Global Loading Spinner */}
            <DashboardLoadingSpinner isVisible={isGlobalLoading} />
            {/* MÓDULO SUPERIOR FIJO: LOGO Y PERFILADOR DE RIESGO */}
            <div className="fixed-top-module">
                <div className="dashboard-content-wrapper relative"> {/* Added relative positioning */}
                    <div className="flex justify-between items-center mb-2">
                        <div className="inline-flex rounded-full bg-gray-800/70 border border-gray-700/60 shadow-sm overflow-hidden">
                            {languageOptions.map(({ code, label }) => (
                                <button
                                    key={code}
                                    onClick={() => handleLanguageChange(code)}
                                    className={`px-3 py-1 text-xs font-semibold transition-colors ${activeLanguage === code ? 'text-white' : 'text-gray-300 hover:text-white hover:bg-gray-700/70'}`}
                                    style={activeLanguage === code ? { backgroundColor: '#d2aa3a' } : {}}
                                    aria-label={activeLanguage === code ? `${languageNames[code]} selected` : `Switch to ${languageNames[code]}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        {/* Account and Other Dashboards Buttons */}
                        <div className="flex items-center gap-2">
                            {/* Other Dashboards Button */}
                            <button
                                onClick={() => navigate('/account')}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-800/70 border border-gray-700/60 text-gray-300 hover:text-white hover:bg-gray-700/70 transition-colors text-xs font-semibold shadow-sm"
                                aria-label={headerText.otherDashboardsButtonAria || 'Go to Other Dashboards'}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="14" width="7" height="7"></rect>
                                    <rect x="3" y="14" width="7" height="7"></rect>
                                </svg>
                                <span className="hidden sm:inline">{headerText.otherDashboardsButton || 'Other Dashboards'}</span>
                            </button>
                            {/* Account Button */}
                            <button
                                onClick={() => navigate('/account')}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-800/70 border border-gray-700/60 text-gray-300 hover:text-white hover:bg-gray-700/70 transition-colors text-xs font-semibold shadow-sm"
                                aria-label={headerText.accountButtonAria || 'Go to Account'}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                                <span className="hidden sm:inline">{headerText.accountButton || 'Account'}</span>
                            </button>
                        </div>
                    </div>
                    
                    {/* --- REMOVED: Tutorial Button --- */}
                    {/* <button
                        onClick={() => setShowTutorial(true)}
                        className="absolute top-5 right-4 z-50 p-2 rounded-full text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors"
                        aria-label="Show tutorial"
                    > ... </button>
                    */}


                    {/* CONDITIONAL HEADER/LOGO - ONLY SHOW WHEN EXPANDED */}
                    {isRiskExpanded && (
                        <header className="flex flex-col items-center mb-4">
                            <div className="flex items-center justify-center text-3xl font-bold tracking-wider relative"> {/* Make this relative */}
                                {/* --- NEW: Tutorial Button (MOVED here) --- */}
                                <button
                                    onClick={() => setShowTutorial(true)}
                                    className="absolute -left-12 top-1/2 -translate-y-1/2 p-2 rounded-full text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors"
                                    aria-label={headerText.tutorialButtonAria || 'Show tutorial'}
                                >
                                    {/* Question Mark Icon */}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                    </svg>
                                </button>
                                
                                <img src={mixedHeaderSrc} alt="Altradenative" className="h-12 w-auto" />
                            </div>
                            <div className="text-sm text-gray-400 mt-1">{taglineText}</div>
                        </header>
                    )}

                    {/* Conditional Content */}
                    {isRiskExpanded ? (
                        <>
                            <hr className="custom-hr mb-6" />
                            {/* MAIN CONTENT: RISK PROFILE SECTION (EXPANDED) */}
                            <div className="flex flex-col md:flex-row gap-10">
                                {/* LEFT COLUMN: RISK SELECTOR */}
                                <div className="w-full md:w-2/5">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-white text-xl font-normal">{riskText.sectionTitle || 'Your Risk Profile'}</h3>
                                        <button 
                                            onClick={toggleRiskExpanded}
                                            className="p-2 rounded-full text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
                                            aria-label={headerText.hideDetailsAria || 'Hide Details'}
                                        >
                                            {/* Chevron Up Icon */}
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
                                        </button>
                                    </div>
                                    <div className="risk-slider">
                                        {/* SLIDER TRACK */}
                                        <div id="riskSliderTrack" className="risk-slider-container">
                                            {/* Handle Position is controlled by state and inline style */}
                                            {riskProfilesData && (() => {
                                                const riskConfig = getRiskProfileConfig(risk, performanceData.native_returns, riskDescriptions);
                                                return (
                                                    <>
                                                        <div 
                                                            id="sliderHandle" 
                                                            className={`slider-handle-active ${riskConfig.cssClass}`}
                                                            style={{ left: riskConfig.position }}
                                                        ></div>
                                                        
                                                        {/* LABELS OVERLAY (Click areas) */}
                                                        {Object.keys(riskProfilesData).map(key => (
                                                            <div 
                                                                key={key}
                                                                data-risk={key}
                                                                className={`risk-label label-${key.toLowerCase()} cursor-pointer ${risk === key ? 'active' : ''}`}
                                                                onClick={() => updateRiskProfile(key)}
                                                            >
                                                                {riskLabels[key] || key}
                                                            </div>
                                                        ))}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    {/* <button 
                                        onClick={toggleRiskExpanded}
                                        className="mt-4 text-xs font-semibold text-gray-400 hover:text-gray-200 transition-colors"
                                    >
                                        [Hide Details]
                                    </button> */}
                                </div>

                                {/* RIGHT COLUMN: RISK DESCRIPTION */}
                                <div className="w-full md:w-3/5">
                                    <div id="riskDescriptionContainer" className="risk-description text-gray-300 space-y-4">
                                        {riskProfilesData ? getRiskProfileConfig(risk, performanceData.native_returns, riskDescriptions).description : <div>{riskText.loading || 'Loading...'}</div>}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        // MAIN CONTENT: RISK PROFILE SECTION (MAX COLLAPSED)
                        <div className="flex flex-col gap-2">
                            {/* Removed custom-hr my-4 to reduce height */}
                            {/* --- UPDATED JSX FOR ALTERNATIVE 2 --- */}
                            <div 
                                className="collapsed-bar"
                                /* Removed all the inline styles */
                            >
                                {/* Left: Logo and Title */}
                                <div className="flex flex-col justify-center px-4">
                                    <div className="flex items-center text-xl font-bold tracking-wider">
                                        <img src={mixedHeaderSrc} alt="Altradenative" style={{ height: '22.4px', width: 'auto' }} />

                                        {/* --- NEW: Tutorial Button (MOVED here) --- */}
                                        <button
                                            onClick={() => setShowTutorial(true)}
                                            className="ml-2 p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors"
                                            aria-label={headerText.tutorialButtonAria || 'Show tutorial'}
                                        >
                                            {/* Question Mark Icon (smaller) */}
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="text-xs text-gray-400 mt-0.5">{collapsedTaglineText}</div>
                                </div>
                                
                                {/* Center: Profile */}
                                <div className="flex-grow flex items-center justify-center gap-2 px-4 border-x border-gray-700 h-full">
                                    <span className="text-white text-lg font-semibold hidden sm:inline">
                                        {headerText.profileLabel || 'Profile:'}
                                    </span>
                                    <span 
                                        className="font-bold px-3 py-1 rounded-full text-sm"
                                        style={{ 
                                            color: `var(--handle-${risk.toLowerCase()}-color)`,
                                            backgroundColor: `var(--handle-${risk.toLowerCase()}-color-faded)`
                                        }}
                                    >
                                        {riskProfilesData ? getRiskProfileConfig(risk, performanceData.native_returns, riskDescriptions).title : (riskLabels[risk] || risk)}
                                    </span>
                                </div>

                                {/* Right: Button */}
                                <div className="px-3">
                                    <button 
                                        onClick={toggleRiskExpanded}
                                        className="p-2 rounded-full text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
                                        aria-label={headerText.showDetailsAria || 'Show Details'}
                                    >
                                        {/* Chevron Down Icon */}
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* SCROLLEABLE DASHBOARD CONTENT */}
            <div className={`scrollable-dashboard-body ${isRiskExpanded ? '' : 'collapsed'}`}>
                <div className="scrollable-inner-container">

                    {/* ========================================================== */}
                    {/* MÓDULO 1: PORTFOLIO & PERFORMANCE */}
                    {/* ========================================================== */}
                    <div className="dashboard-module portfolio-module">
                        
                        {/* --- FIX: New wrapper for top row --- */}
                        <div className="flex flex-col md:flex-row w-full gap-4 md:gap-8">
                            {/* Portfolio Column */}
                            <div className="flex flex-col w-full md:w-1/3">
                                <h3 className="text-xl font-semibold mb-6 text-gray-300">{portfolioText.title || 'Portfolio allocation now'}</h3>
                                
                                {/* Dynamically render asset items using the new simplified Donut logic */}
                                {currentAllocation.map((asset, index) => (
                                    <div key={index} className="asset-item" >
                                        <div 
                                            className="donut-chart" 
                                            style={renderIndividualDonutStyle(asset.percent, asset.colorVar)}
                                        >
                                            <span className="donut-percent">{asset.percent}%</span>
                                        </div>
                                        <div className="flex flex-col text-sm text-gray-400">
                                            <span className="text-gray-200 font-semibold">{asset.name}</span>
                                            <span className="text-xs">{asset.ticker}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Performance Column (ECharts Line Chart) */}
                            <div className="w-full md:w-2/3">
                                <div className="flex justify-between items-start mb-6">
                                    <h3 className="text-xl font-semibold text-gray-300">{portfolioText.performanceTitle || 'Algorithmic adaptative performance in time'}</h3>
                                </div>

                                <div className="performance-column" id="performanceColumn">
                                    {/* Top Legends */}
                                    <div className="chart-legends-top">
                                        <span className="chart-legend-item legend-spy">SPY</span>
                                        <span className="chart-legend-item legend-native">ALTRADENATIVE</span>
                                    </div>
                                    
                                    {/* ECharts Container */}
                                    <div ref={performanceRef} id="echartContainer"></div>

                                    {/* X-Axis Title */}
                                    <div className="chart-x-axis pt-2 text-xs text-gray-400 font-semibold">
                                        {portfolioText.tradingDays || 'Trading Days'}
                                    </div>
                                </div>
                                
                                {/* Chart Description (Outside Chart Area) */}
                                <p
                                    className="chart-description"
                                    dangerouslySetInnerHTML={renderRichText(portfolioText.chartDescription)}
                                />
                            </div>
                        </div>
                        {/* --- End of wrapper --- */}


                        {/* --- NEW: Live Quote Widget (Now sits correctly below the wrapper) --- */}
                        <div className="w-full mt-6 pt-6 border-t border-gray-700/80">
                            {/* --- NEW: Title with Market Status --- */}
                            <div className="flex items-center gap-3 mb-4">
                                <h3 className="text-xl font-semibold text-gray-300">{marketSnapshotText.title || 'Market Snapshot'}</h3>
                                <div className="flex items-center gap-2">
                                    <div className={`market-status-indicator ${marketStatus.isOpen ? 'open' : 'closed'}`}></div>
                                    <span className={`text-sm font-medium ${marketStatus.isOpen ? 'text-green-400' : 'text-red-400'}`}>
                                        {marketStatus.statusLabel}
                                    </span>
                                </div>
                            </div>
                            <LiveQuoteWidget quotes={quotes} />
                            <p className="text-xs text-gray-500 mt-3">{delayDisclaimerText} Price change in % may not be accurate if the market is not open yet.</p>
                        </div>
                    </div>

                    {/* ========================================================== */}
                    {/* MÓDULO 2: FULL EXPOSURE & EXPOSURE IN TIME */}
                    {/* ========================================================== */}
                    <div className="dashboard-module flex flex-col md:flex-row justify-between items-start p-6">
                        
                        {/* LEFT COLUMN: Full Exposure Chart (Portfolio Weights) */}
                        <div className="w-full md:w-1/2 flex flex-col justify-center md:justify-start pt-2 order-2 md:order-1">
                            <h3 className="text-xl font-semibold mb-2 text-gray-300">{allocationText.title || 'Allocation Overview'}</h3>
                            <div ref={exposureRef} className="exposure-chart-wrapper min-h-[325px]">
                                {/* ECharts Exposure Chart will render here */}
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Exposure in Time Chart */}
                        <div className="w-full md:w-1/2 flex flex-col justify-start pt-2 order-1 md:order-2 md:min-h-[350px]">
                            {/* Render only when CSS colors are ready for ECharts initialization */}
                            {isCssReady && (
                                <div className="new-content-area w-full">
                                    {/* Header with Title */}
                                    <div className="flex items-center gap-2 mb-4">
                                        <h3 className="text-xl font-semibold text-gray-300">{exposureText.title || 'Exposure in Time'}</h3>
                                        
                                        <div className="info-tooltip-container">
                                            {/* Info icon (inline SVG) */}
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                            </svg>
                                            <div
                                                className="info-tooltip-content"
                                                dangerouslySetInnerHTML={renderRichText(exposureText.tooltip)}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Timeframe Buttons */}
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {[10, 30, 120, 250].map((days) => (
                                            <button
                                                key={days}
                                                onClick={() => {
                                                    if (exposureTimelineZoomControlRef.current?.setZoomByDays) {
                                                        exposureTimelineZoomControlRef.current.setZoomByDays(days);
                                                    }
                                                }}
                                                className="px-3 py-1.5 text-xs font-medium rounded-md bg-gray-800/70 border border-gray-700/60 text-gray-300 hover:text-white hover:bg-gray-700/70 transition-colors"
                                            >
                                                Last {days} days
                                            </button>
                                        ))}
                                    </div>

                                    {exposureTimelineData.timeline && exposureTimelineData.timeline.length > 0 ? (
                                        (() => {
                                            const timeline = exposureTimelineData.timeline;
                                            console.log(`[Render] Rendering ExposureTimelineChart with ${timeline.length} data points`);
                                            // Verify no data reduction happened during prop passing
                                            if (timeline.length >= 2) {
                                                const date1 = new Date(timeline[0].date);
                                                const date2 = new Date(timeline[1].date);
                                                const daysDiff = Math.round((date2 - date1) / (1000 * 60 * 60 * 24));
                                                if (daysDiff > 1) {
                                                    console.error(`[Render] ERROR: Timeline data has ${daysDiff}-day intervals at render time!`);
                                                }
                                            }
                                            // #region agent log
                                            const lastTimelineItemRender = timeline.length > 0 ? timeline[timeline.length - 1] : null;
                                            fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DashboardHybrid.jsx:4240',message:'Render: Last item in timeline before passing to ExposureTimelineChart','data':{timelineLength:timeline.length,lastTimelineItem:lastTimelineItemRender,currentDataProp:exposureTimelineData.current},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
                                            // #endregion
                                            return (
                                                <ExposureTimelineChart
                                                    key={`exposure-timeline-${risk}`}
                                                    chartRef={exposureTimelineRef}
                                                    timelineData={timeline}
                                                    currentData={exposureTimelineData.current}
                                                    risk={risk}
                                                    getCssVariable={getCssVariable}
                                                    zoomControlRef={exposureTimelineZoomControlRef}
                                                />
                                            );
                                        })()
                                    ) : (
                                        <div className="text-gray-400 text-center mt-4 py-20">{loadingText.exposure || 'Loading exposure data...'}</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ========================================================== */}
                    {/* INVESTMENT ALLOCATION CALCULATOR */}
                    {/* ========================================================== */}
                    {isCssReady && (
                        <InvestmentAllocationCalculator
                            currentAllocation={currentAllocation}
                            quotes={quotes}
                            getCssVariable={getCssVariable}
                            calculatorText={investmentCalculatorText}
                        />
                    )}

                    {/* ========================================================== */}
                    {/* MÓDULO 3: CORRELATION SCATTER CHART (Beta) */}
                    {/* ========================================================== */}
                    <div className="dashboard-module">
                        <h3 className="text-xl font-semibold mb-4 text-gray-300">
                            {scatterText.title || 'Risk / Return vs Global Market Benchmark (SPY ETF)'}
                        </h3>
                        
                        {isCssReady && (
                            <div className="scatter-chart-container">
                                <div 
                                    ref={scatterRef} 
                                    style={{ width: '100%', height: '100%' }}
                                >
                                    {/* ECharts Scatter Chart renders here */}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ========================================================== */}
                    {/* MÓDULO 4: RELATIVE PERFORMANCE (New) */}
                    {/* ========================================================== */}
                    <div className="dashboard-module">
                        <h3 className="text-xl font-semibold mb-4 text-gray-300">
                            {relativePerformanceText.title || 'Relative Performance'}
                        </h3>
                        {isCssReady && (
                            <RelativePerformanceChart 
                                risk={risk}
                                getCssVariable={getCssVariable}
                                isCssReady={isCssReady}
                                chartContextRef={chartContextRef}
                                relativePerformanceData={relativePerformanceData}
                                chartTitles={relativePerformanceText.chartTitles}
                            />
                        )}
                    </div>

                    {/* ========================================================== */}
                    {/* MÓDULO 5: VOLATILITY */}
                    {/* ========================================================== */}
                    <div className="dashboard-module">
                        <h3 className="text-xl font-semibold mb-4 text-gray-300">
                            {volatilityText.title || 'Volatility'}
                        </h3>
                        {isCssReady && (
                            <VolatilityChart
                                risk={risk}
                                getCssVariable={getCssVariable}
                                isCssReady={isCssReady}
                                chartContextRef={chartContextRef}
                                volatilityData={volatilityData}
                                chartTitles={volatilityText.chartTitles}
                            />
                        )}
                    </div>

                    {/* ========================================================== */}
                    {/* MÓDULO 6: PORTFOLIO SUMMARY (New) */}
                    {/* ========================================================== */}
                    <div className="dashboard-module">
                        <h3 className="text-xl font-semibold mb-4 text-gray-300">
                            {summaryText.title || 'Final Assessment'}
                        </h3>
                        {isCssReady && (
                            (relativePerformanceData && relativePerformanceData[risk] && volatilityData && volatilityData[risk]) ? (
                                <PortfolioSummary
                                    risk={risk}
                                    currentAllocation={currentAllocation}
                                    performanceData={relativePerformanceData}
                                    volatilityData={volatilityData}
                                    marketStatus={marketStatus}
                                    quotes={quotes}
                                    correlationData={correlationData}
                                    exposureTimelineData={exposureTimelineData}
                                    performanceTimeSeries={performanceData}
                                />
                            ) : (
                                <div className="text-gray-400 text-center">{loadingText.summary || 'Loading summary data...'}</div>
                            )
                        )}
                    </div>
                    
                    {/* ========================================================== */}
                    {/* LEGAL DISCLAIMER */}
                    {/* ========================================================== */}
                    <div className="dashboard-module bg-[#1f2327] border border-gray-700 text-gray-300 text-[0.63rem] leading-relaxed space-y-3">
                        <h3 className="text-base font-semibold text-gray-100 tracking-wide uppercase mb-3">{legalDisclaimerText.title || 'Legal Disclaimer'}</h3>
                        {legalParagraphs.map((paragraph, index) => (
                            <p key={index} className="text-justify hyphens-auto break-words" style={{ textJustify: 'inter-word', lineHeight: '1.8' }} dangerouslySetInnerHTML={renderRichText(paragraph)} />
                        ))}
                    </div>

                    {/* Padding for footer clearance */}
                    <div className="h-10"></div>
                </div>
            </div>

            {/* MÓDULO INFERIOR FIJO: FOOTER BAR */}
            <footer className="footer-bar">
                <div className="flex items-center text-sm font-bold text-white">
                    <img src={mixedFooterSrc} alt="Altradenative" className="h-6" />
                </div>
            </footer>

            {/* --- NEW: Render Tutorial Modal --- */}
            <DashboardTutorial
                isOpen={showTutorial}
                onClose={handleCloseTutorial}
                showOnStartup={showTutorialOnStartup}
                onToggleStartup={handleToggleStartup}
            />

            {/* --- NEW: Virtual Assistant --- */}
            <VirtualAssistant
                isOpen={showAssistant}
                onClose={() => setShowAssistant(false)}
                dashboardData={{
                    risk,
                    currentAllocation,
                    exposureTimelineData,
                    correlationData,
                    performanceData: relativePerformanceData,
                    volatilityData,
                    relativePerformanceData,
                    marketStatus,
                    quotes,
                    marketContext
                }}
            />

            {/* --- NEW: Floating Action Button for Assistant --- */}
            {!showAssistant && (
                <button
                    onClick={() => setShowAssistant(true)}
                    className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center z-[9999]"
                    aria-label={assistantButtonText.aria || 'Open Investment Assistant'}
                    title={assistantButtonText.title || 'Ask me anything about your dashboard'}
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                </button>
            )}
        </div>
    );
}

